<template>
  <div class="collection-summary-root">
    <div v-if="collectionStagesVisual" class="financial-summary-stages rounded border mb-4">
            <div class="row g-0">
              <div
                class="col-lg-4 financial-summary-stages__col1"
                :class="{ 'financial-summary-stages__col--done': stage1HeaderDone }"
              >
                <div class="p-3 h-100">
                  <div
                    class="d-flex align-items-center gap-2 mb-3 financial-summary-stages__stage-head"
                    :class="{ 'financial-summary-stages__stage-head--done': stage1HeaderDone }"
                  >
                    <span
                      class="financial-summary-stages__badge"
                      :class="{ 'financial-summary-stages__badge--done': stage1HeaderDone }"
                    >1</span>
                    <span
                      class="small text-uppercase fw-bold"
                      :class="stage1HeaderDone ? 'text-success' : 'text-muted'"
                    >{{ $t("financialSummary.stages.step1Short") }}</span>
                  </div>
                  <div
                    v-if="collectionStagesShowClientUnderStep1"
                    class="small mb-3 w-100 d-flex flex-wrap align-items-center gap-2"
                  >
                    <div class="text-truncate min-w-0 flex-grow-1">
                      <span class="text-muted me-1">{{ $t("financialSummary.summaryClientLabel") }}</span>
                      <a
                        v-if="collectionStagesResolvedClientId"
                        href="#"
                        class="financial-summary-stages__client-under-step1 text-body fw-semibold text-decoration-none"
                        :title="$t('financialSummary.openContactNewTab')"
                        @click.prevent="openCollectionStagesClientContact"
                      >
                        {{ collectionStagesResolvedClientDisplayName }}
                        <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                      </a>
                      <span v-else class="fw-semibold text-body">{{ collectionStagesResolvedClientDisplayName }}</span>
                      <NotaComercialPendingBadge
                        v-if="collectionStagesResolvedClientId"
                        class="ms-1"
                        :contact-id="String(collectionStagesResolvedClientId)"
                        :contact-name="collectionStagesResolvedClientDisplayName"
                      />
                    </div>
                    <div class="d-flex flex-wrap align-items-center gap-1 flex-shrink-0">
                      <button
                        v-if="fobNcShortcutUiAllowed && collectionStagesResolvedClientId"
                        type="button"
                        class="btn btn-sm btn-outline-secondary flex-shrink-0 py-0 px-1"
                        style="font-size: 0.7rem"
                        @click.stop="openFobNotaComercialClientModal"
                      >
                        <i class="fas fa-plus me-1" aria-hidden="true"></i>
                        {{ $t("financialSummary.fobNotaComercial.notaComercialButton") }}
                      </button>
                      <NotaComercialAssignButton
                        v-if="collectionStagesResolvedClientId && fobNcProformId"
                        :contact-id="String(collectionStagesResolvedClientId)"
                        side="client"
                        target-type="proform"
                        :target-id="fobNcProformId"
                        @assigned="onNotaComercialAssigned"
                      />
                    </div>
                  </div>
                  <template v-if="collectionStagesVisual.stage1.advancePct > 0">
                    <div class="mb-2 text-body lh-sm financial-summary-stages__stage1-advance-line" style="font-size: 0.95rem">
                      <span class="fw-semibold tabular-nums">
                        <span
                          class="financial-summary-stages__advance-pct-emphasis financial-summary-stages__advance-pct-emphasis--proform"
                        >{{ moneyPrefix }}{{ formatNumber(collectionStagesVisual.stage1.expectedAmount) }}</span><button
                          type="button"
                          class="btn btn-link btn-sm p-0 ms-1 align-baseline financial-summary-stages__advance-pct-edit"
                          :title="$t('financialSummary.stages.advancePctEditHint')"
                          :aria-label="$t('financialSummary.stages.advancePctEditHint')"
                          :disabled="savingProformAdvance"
                          @click="openProformAdvanceModal"
                        >
                          <i
                            :class="savingProformAdvance ? 'fas fa-spinner fa-spin' : 'fas fa-pencil-alt'"
                            aria-hidden="true"
                          ></i>
                        </button><span>{{
                          $t("financialSummary.stages.stage1AdvanceSummaryLineAfterAmount", {
                            total: moneyPrefix + formatNumber(stage1DisplayTotalProform),
                            pct: formatAdvancePct(collectionStagesVisual.stage1.advancePct),
                          })
                        }}</span>
                      </span>
                    </div>
                    <div class="text-muted mb-3 d-flex align-items-center flex-wrap gap-2 financial-summary-stages__containers">
                      <span>
                        <i class="fas fa-cube text-secondary me-1" aria-hidden="true"></i>
                        <span class="fw-semibold text-body tabular-nums">{{ proformContainerCountRounded }}</span>
                        {{ $t("Qty containers") }}
                        <span
                          v-if="proformContainerNumbersText"
                          class="fw-semibold text-body financial-summary-stages__container-numbers"
                          :title="$t('financialSummary.stages.containerNumbersHint')"
                        >{{ proformContainerNumbersText }}</span>
                      </span>
                    </div>
                    <div class="row g-2 small mb-2">
                      <div class="col-6">
                        <div class="text-muted text-uppercase" style="font-size: 0.65rem">{{ $t("financialSummary.stages.collectedShort") }}</div>
                        <div class="fs-6 fw-semibold text-success">
                          {{ moneyPrefix }}{{ formatNumber(collectionStagesVisual.stage1.collectedAmount) }}
                          <button
                            v-if="collectionStagesVisual.stage1.collectedAmount > 0.01"
                            type="button"
                            class="btn btn-link btn-sm p-0 ms-1 align-baseline"
                            :title="$t('financialSummary.viewCollections')"
                            @click="viewCollectionDetails"
                          >
                            <i class="fas fa-eye text-info" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-muted text-uppercase" style="font-size: 0.65rem">{{ $t("financialSummary.stages.remainingShort") }}</div>
                        <div class="d-flex flex-wrap align-items-center gap-2">
                          <div
                            class="fs-6 fw-semibold"
                            :class="collectionStagesVisual.stage1.remainingAmount > 0.01 ? 'text-warning' : 'text-success'"
                          >
                            {{ moneyPrefix }}{{ formatNumber(collectionStagesVisual.stage1.remainingAmount) }}
                          </div>
                          <p
                            v-if="stage1OverCollectedAmount() > 0.01"
                            class="small text-warning mb-0 mt-1 lh-sm"
                            style="font-size: 0.7rem"
                          >
                            {{
                              $t("financialSummary.stages.overCollectedAdvanceHint", {
                                amount: moneyPrefix + formatNumber(stage1OverCollectedAmount()),
                              })
                            }}
                          </p>
                          <button
                            v-if="
                              canCollect &&
                                !fobNotaComercialShortcuts &&
                                collectionStagesVisual.stage1.remainingAmount > 0.01 &&
                                proformContainerCountRounded >= 1
                            "
                            type="button"
                            class="btn btn-sm btn-outline-warning"
                            :disabled="showAddCollectionModal"
                            :title="$t('financialSummary.stages.quickCollectRemainingHint')"
                            @click="postQuickRemainingAdvance"
                          >
                            <i class="fas fa-bolt me-1" aria-hidden="true"></i>
                            {{ $t("financialSummary.stages.quickCollectRemaining") }}
                          </button>
                        </div>
                      </div>
                    </div>
                    <!-- Falta adelanto pero la plata ya entró, cobrada contra la factura de
                         los embarques: se avisa que no hay que cobrar de nuevo ni borrar nada,
                         y el botón hace el reordenamiento. -->
                    <div
                      v-if="advanceShortWithMoneyOnShippingInvoices"
                      class="alert alert-info py-2 px-2 small mb-2"
                    >
                      <div class="fw-semibold mb-1">
                        <i class="fas fa-info-circle me-1" aria-hidden="true"></i>
                        {{ $t("financialSummary.stages.advanceShortMoneyOnInvoicesTitle") }}
                      </div>
                      <div class="mb-2 lh-sm">
                        {{
                          $t("financialSummary.stages.advanceShortMoneyOnInvoicesBody", {
                            missing: moneyPrefix + formatNumber(advanceShortWithMoneyOnShippingInvoices.remaining),
                            onInvoices: moneyPrefix + formatNumber(advanceShortWithMoneyOnShippingInvoices.invoiceOnly),
                          })
                        }}
                      </div>
                      <button
                        v-if="canCollect"
                        type="button"
                        class="btn btn-sm btn-primary fw-semibold shadow-sm"
                        :disabled="redistributeInProgress"
                        :title="$t('financialSummary.stages.reorderCollectsHint')"
                        @click="promptMoveInvoiceMoneyToAdvance"
                      >
                        <i class="fas fa-random me-1" aria-hidden="true"></i>
                        {{ $t("financialSummary.stages.reorderCollects") }}
                      </button>
                    </div>
                    <div class="progress financial-summary-stages__progress mb-2" style="height: 0.4rem">
                      <div
                        class="progress-bar"
                        :class="proformStagesProgressClass"
                        role="progressbar"
                        :style="{ width: proformStagesProgressWidth + '%' }"
                        :aria-valuenow="proformStagesProgressWidth"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    <div
                      v-if="collectionStage1ShowNotaImpact"
                      class="financial-summary-stages__ship-section financial-summary-stages__ship-section--advance mb-2 mt-2"
                      role="note"
                    >
                      <div class="financial-summary-stages__ship-section-title">
                        {{ $t("financialSummary.stages.notaImpactBoxCaption") }}
                      </div>
                      <div
                        class="small text-uppercase text-muted mb-2"
                        style="font-size: 0.58rem; letter-spacing: 0.03em"
                      >
                        {{ $t("financialSummary.stages.notaImpactAdvanceMetaCaption") }}
                      </div>
                      <template v-if="fobNcLinkedClientNotesProformCreditOnly.length">
                        <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                          {{ $t("financialSummary.stages.notaImpactAdvanceCreditRowTitle") }}
                        </div>
                        <div class="d-flex flex-column gap-1 w-100 align-items-stretch mb-2">
                          <a
                            v-for="n in fobNcLinkedClientNotesProformCreditOnly"
                            :key="'fob-nc-pf-cr-' + n._id"
                            :href="fobNcNotaComercialViewHref(n._id)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                            :class="{
                              'financial-summary-stages__nota-proform-drag':
                                fobNcShortcutUiAllowed && !redistributeInProgress,
                            }"
                            :draggable="fobNcShortcutUiAllowed && !redistributeInProgress"
                            :title="fobNcLinkedNoteTitleAttr(n)"
                            @dragstart.stop="onNotaProformDragStart($event, n)"
                            @dragend="onNotaProformDragEnd"
                          >
                            <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                              <i
                                v-if="fobNcShortcutUiAllowed && !redistributeInProgress"
                                class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                style="font-size: 0.65rem"
                                aria-hidden="true"
                              ></i>
                              <i
                                class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                style="font-size: 0.65rem"
                                aria-hidden="true"
                              ></i>
                              <span class="small text-body text-break min-w-0">{{
                                fobNcLinkedNoteDisplayName(n, 80)
                              }}</span>
                            </span>
                            <span
                              class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                            >
                              <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(n.appliedTotal) }}</strong>
                              <i
                                v-if="fobNcShortcutUiAllowed"
                                class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                style="font-size: 0.72rem; cursor: pointer"
                                role="button"
                                tabindex="0"
                                :title="$t('notaComercial.unapply.iconTitle')"
                                @click.stop.prevent="
                                  fobNcUnapplyNote(n, {
                                    targetType: 'proform',
                                    targetId: fobNcProformId,
                                    operationName: null,
                                  })
                                "
                              ></i>
                              <i
                                class="fas fa-eye text-info flex-shrink-0"
                                style="font-size: 0.72rem"
                                :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                aria-hidden="true"
                              ></i>
                              <i
                                v-if="fobNcShortcutUiAllowed && fobNcLinkedNoteIsCreditNote(n)"
                                class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                style="font-size: 0.72rem; cursor: pointer"
                                role="button"
                                tabindex="0"
                                :title="$t('financialSummary.fobNotaComercial.editCommercialNote')"
                                aria-hidden="true"
                                @click.stop.prevent="openFobNcEditModal(n, { mode: 'proform' })"
                                @mousedown.stop
                              ></i>
                            </span>
                          </a>
                        </div>
                      </template>
                      <div
                        v-else-if="stage1ClientNotaCreditProformApplied > 0.01"
                        class="small text-muted mb-2"
                      >
                        {{
                          $t("financialSummary.stages.notaImpactAdvanceCredit", {
                            amount: moneyPrefix + formatNumber(stage1ClientNotaCreditProformApplied),
                          })
                        }}
                      </div>
                      <template v-if="fobNcLinkedClientNotesProformDebitOnly.length">
                        <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                          {{ $t("financialSummary.stages.notaImpactAdvanceDebitRowTitle") }}
                        </div>
                        <div class="d-flex flex-column gap-1 w-100 align-items-stretch">
                          <a
                            v-for="n in fobNcLinkedClientNotesProformDebitOnly"
                            :key="'fob-nc-pf-db-' + n._id"
                            :href="fobNcNotaComercialViewHref(n._id)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                            :class="{
                              'financial-summary-stages__nota-proform-drag':
                                fobNcShortcutUiAllowed && !redistributeInProgress,
                            }"
                            :draggable="fobNcShortcutUiAllowed && !redistributeInProgress"
                            :title="fobNcLinkedNoteTitleAttr(n)"
                            @dragstart.stop="onNotaProformDragStart($event, n)"
                            @dragend="onNotaProformDragEnd"
                          >
                            <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                              <i
                                v-if="fobNcShortcutUiAllowed && !redistributeInProgress"
                                class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                style="font-size: 0.65rem"
                                aria-hidden="true"
                              ></i>
                              <i
                                class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                style="font-size: 0.65rem"
                                aria-hidden="true"
                              ></i>
                              <span class="small text-body text-break min-w-0">{{
                                fobNcLinkedNoteDisplayName(n, 80)
                              }}</span>
                            </span>
                            <span
                              class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                            >
                              <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(n.appliedTotal) }}</strong>
                              <i
                                v-if="fobNcShortcutUiAllowed"
                                class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                style="font-size: 0.72rem; cursor: pointer"
                                role="button"
                                tabindex="0"
                                :title="$t('notaComercial.unapply.iconTitle')"
                                @click.stop.prevent="
                                  fobNcUnapplyNote(n, {
                                    targetType: 'proform',
                                    targetId: fobNcProformId,
                                    operationName: null,
                                  })
                                "
                              ></i>
                              <i
                                class="fas fa-eye text-info flex-shrink-0"
                                style="font-size: 0.72rem"
                                :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                aria-hidden="true"
                              ></i>
                            </span>
                          </a>
                        </div>
                      </template>
                      <div
                        v-else-if="stage1ClientNotaDebitProformApplied > 0.01"
                        class="small text-muted"
                      >
                        {{
                          $t("financialSummary.stages.notaImpactAdvanceDebit", {
                            amount: moneyPrefix + formatNumber(stage1ClientNotaDebitProformApplied),
                          })
                        }}
                      </div>
                    </div>
                  </template>
                  <div v-else class="mb-3">
                    <div
                      class="alert alert-warning py-2 px-2 small mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2"
                    >
                      <span>
                        <i class="fas fa-percentage me-1" aria-hidden="true"></i>
                        {{ $t("financialSummary.stages.noAdvancePct") }}
                      </span>
                      <button
                        type="button"
                        class="btn btn-sm btn-light flex-shrink-0 fw-semibold financial-summary-stages__advance-pct-set"
                        :disabled="savingProformAdvance"
                        @click="openProformAdvanceModal"
                      >
                        <i
                          :class="savingProformAdvance ? 'fas fa-spinner fa-spin me-1' : 'fas fa-pencil-alt me-1'"
                          aria-hidden="true"
                        ></i>
                        {{
                          savingProformAdvance
                            ? $t("financialSummary.stages.advancePctSaving")
                            : $t("financialSummary.stages.advancePctSetButton")
                        }}
                      </button>
                    </div>
                    <div
                      class="d-flex align-items-center justify-content-between gap-2 mb-2 py-2 px-2 rounded border bg-white"
                    >
                      <span class="small text-muted mb-0">
                        <i class="fas fa-file-invoice-dollar me-1 text-secondary" aria-hidden="true"></i>
                        {{ $t("financialSummary.totalProform") }}
                      </span>
                      <span class="fs-6 fw-semibold text-body tabular-nums">{{ moneyPrefix }}{{ formatNumber(stage1DisplayTotalProform) }}</span>
                    </div>
                    <div
                      class="d-flex align-items-center justify-content-between gap-2 mb-2 py-2 px-2 rounded border bg-light"
                    >
                      <span class="small text-muted mb-0">
                        <i class="fas fa-cube me-1 text-secondary" aria-hidden="true"></i>
                        {{ $t("Qty containers") }}
                      </span>
                      <span class="text-end financial-summary-stages__containers">
                        <span class="fw-semibold text-body tabular-nums">{{ proformContainerCountRounded }}</span>
                        <span
                          v-if="proformContainerNumbersText"
                          class="fw-semibold text-body financial-summary-stages__container-numbers ms-1"
                          :title="$t('financialSummary.stages.containerNumbersHint')"
                        >{{ proformContainerNumbersText }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="showStage1DragAndDropSection"
                    class="financial-summary-stages__chips mt-2 mb-3"
                  >
                    <div v-if="showStage1ProformOnlyChips" class="small text-muted mb-2">{{ $t("financialSummary.stages.dragChipsHint") }}</div>
                    <div
                      v-if="showAdvanceReturnToProformDropzone"
                      class="financial-summary-stages__dropzone financial-summary-stages__dropzone--stage1-full mb-2 rounded border border-dashed text-center small text-muted"
                      :class="{ 'financial-summary-stages__drop-target--active': stagesDndReturnDropzoneActive }"
                      @dragenter.prevent.stop="onAdvanceDragEnterReturnDropzone"
                      @dragleave.stop="onAdvanceDragLeaveReturnDropzone"
                      @dragover.prevent.stop="onAdvanceDragOverReturnDropzone"
                      @drop.prevent="onAdvanceDropReturnToProform"
                    >
                      {{ $t("financialSummary.stages.dropReturnProform") }}
                    </div>
                    <div class="d-flex flex-column gap-1 align-items-stretch w-100">
                      <template v-for="bundle in collectionStagesVisual.stage1CollectLines">
                        <div
                          v-for="line in bundle.rows"
                          v-show="line.amount > 0.005"
                          :key="'s1chip-' + line._id"
                          class="financial-summary-stages__chip financial-summary-stages__chip--stage1-collect border rounded-2 px-2 py-1 text-start d-flex flex-column align-items-stretch gap-1 shadow-sm w-100"
                          :class="
                            canCollect
                              ? 'border-primary bg-white'
                              : 'border-secondary bg-light'
                          "
                          :draggable="canCollect"
                          @dragstart="onAdvanceDragStart($event, { kind: 'proform_only', collectId: bundle.collectId, fromAppliedToId: line._id, maxAmount: line.amount, unapplied: bundle.collectUnapplied })"
                          @dragend="onAdvanceDragEnd"
                        >
                          <div class="d-flex align-items-center flex-wrap gap-1 w-100 min-w-0">
                            <i class="fas fa-grip-vertical text-muted flex-shrink-0" aria-hidden="true"></i>
                            <span class="text-truncate min-w-0 flex-grow-1">{{ bundle.collectName || "—" }}</span>
                            <strong class="small">{{ moneyPrefix }}{{ formatNumber(line.amount) }}</strong>
                            <i
                              class="fas fa-eye text-info flex-shrink-0"
                              style="font-size: 0.75rem"
                              :title="$t('financialSummary.stages.openCollectNewTab')"
                              aria-hidden="true"
                              role="button"
                              tabindex="0"
                              @click.stop.prevent="goToCollect(bundle.collectId)"
                              @mousedown.stop
                            ></i>
                            <i
                              v-if="canCollect"
                              class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                              style="font-size: 0.75rem; cursor: pointer"
                              role="button"
                              tabindex="0"
                              :title="$t('financialSummary.stages.quickEditCollectTitle')"
                              aria-hidden="true"
                              @click.stop.prevent="
                                openQuickEditCollectRow({
                                  collectId: bundle.collectId,
                                  appliedToLineId: line._id,
                                  lineAmount: line.amount,
                                })
                              "
                              @mousedown.stop
                            ></i>
                          </div>
                          <div
                            v-if="
                              canCollect &&
                                collectionStagesVisual.stage1.advancePct > 0 &&
                                collectionStagesVisual.stage2Shippings &&
                                collectionStagesVisual.stage2Shippings.length
                            "
                            class="d-flex flex-column align-items-stretch gap-1 pt-1 mt-1 border-top border-light border-opacity-75"
                          >
                            <button
                              v-for="(ship, sIdx) in collectionStagesVisual.stage2Shippings"
                              :key="'s1assign-' + line._id + '-' + ship.shippingId"
                              type="button"
                              class="btn btn-outline-primary btn-sm py-0 px-1 financial-summary-stages__stage1-assign-ship-btn text-truncate w-100"
                              :title="
                                $t('financialSummary.stages.stage1AssignAdvanceToShippingNamedHint', {
                                  name:
                                    (ship.name && String(ship.name).trim()) ||
                                    $t('financialSummary.stages.stage1AssignAdvanceToShippingTitle', {
                                      n: sIdx + 1,
                                    }),
                                })
                              "
                              @click.stop="assignStage1CollectLineToShippingAdvance(bundle, line, ship)"
                            >
                              {{
                                $t('financialSummary.stages.stage1AssignAdvanceToShippingNamed', {
                                  name:
                                    (ship.name && String(ship.name).trim()) ||
                                    $t('financialSummary.stages.stage1AssignAdvanceToShippingTitle', {
                                      n: sIdx + 1,
                                    }),
                                })
                              }}
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="stage1AdvanceAllocatedRows.length"
                      class="mt-2"
                    >
                      <div class="small text-muted mb-1">
                        {{ $t("financialSummary.stages.stage1AllocatedAdvanceCaption") }}
                      </div>
                      <div class="d-flex flex-column gap-1 w-100 align-items-stretch">
                        <div
                          v-for="row in stage1AdvanceAllocatedRows"
                          :key="'s1alloc-' + row._id"
                          class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--readonly"
                        >
                          <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                            <span class="small text-body text-break min-w-0">{{ row.collectName || "—" }}</span>
                            <span
                              v-if="row.shipName"
                              class="badge bg-light text-secondary border fw-normal flex-shrink-0"
                              style="font-size: 0.62rem"
                              :title="$t('financialSummary.stages.stage1AllocatedAdvanceShipHint', { name: row.shipName })"
                            >{{ row.shipName }}</span>
                          </span>
                          <span class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount">
                            <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(row.amount) }}</strong>
                            <i
                              class="fas fa-eye text-info flex-shrink-0"
                              style="font-size: 0.72rem; cursor: pointer"
                              :title="$t('financialSummary.stages.openCollectNewTab')"
                              aria-hidden="true"
                              role="button"
                              tabindex="0"
                              @click.stop.prevent="goToCollect(row.collectId)"
                              @mousedown.stop
                            ></i>
                            <i
                              v-if="canCollect"
                              class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                              style="font-size: 0.72rem; cursor: pointer"
                              role="button"
                              tabindex="0"
                              :title="$t('financialSummary.stages.quickEditCollectTitle')"
                              aria-hidden="true"
                              @click.stop.prevent="
                                openQuickEditCollectRow({
                                  collectId: row.collectId,
                                  appliedToLineId: row._id,
                                  lineAmount: row.amount,
                                })
                              "
                              @mousedown.stop
                            ></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex flex-wrap gap-2 justify-content-lg-start justify-content-between mt-auto pt-2 border-top border-light">
                    <template v-if="collectionStagesVisual.stage1.advancePct > 0">
                      <button
                        v-if="
                          canCollect &&
                            collectionStagesVisual.stage1.remainingAmount > 0.01
                        "
                        type="button"
                        class="btn btn-sm btn-outline-success"
                        @click="executeAction({ type: 'addCollect' })"
                      >
                        <i class="fas fa-plus me-1" aria-hidden="true"></i>
                        {{
                          fobNotaComercialShortcuts ||
                          collectionStagesVisual.stage1.collectedAmount < 0.01
                            ? $t("financialSummary.actions.addCollection")
                            : $t("financialSummary.actions.receivePayment")
                        }}
                      </button>
                      <button
                        v-if="notaComercialQuickAllowed"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="openNotaComercialNoteModal"
                      >
                        <i class="fas fa-file-invoice me-1" aria-hidden="true"></i>
                        {{ $t("financialSummary.actions.addCommercialNote") }}
                      </button>
                      <span v-else-if="!canCollect && collectionStagesVisual.stage1.remainingAmount > 0.01" class="small text-muted align-self-center">
                        {{ $t("financialSummary.stages.noCollectPermissionHint") }}
                      </span>
                    </template>
                  </div>
                  <!-- Observaciones de la operación: se guarda solo, sin botón guardar -->
                  <div v-if="operationFlagsEnabled" class="mt-2 pt-2 border-top border-light">
                    <div
                      class="d-flex align-items-center justify-content-between gap-2 text-muted text-uppercase small fw-semibold mb-1"
                    >
                      <span>{{ $t("financialSummary.stages.observationsTitle") }}</span>
                      <span v-if="observationsStatus" class="text-lowercase fw-normal">
                        <i
                          v-if="observationsStatus === 'saving'"
                          class="fas fa-spinner fa-spin me-1"
                          aria-hidden="true"
                        ></i>
                        <i
                          v-else-if="observationsStatus === 'saved'"
                          class="fas fa-check me-1 text-success"
                          aria-hidden="true"
                        ></i>
                        {{
                          observationsStatus === "saving"
                            ? $t("financialSummary.stages.observationsSaving")
                            : observationsStatus === "saved"
                              ? $t("financialSummary.stages.observationsSaved")
                              : $t("financialSummary.stages.observationsError")
                        }}
                      </span>
                    </div>
                    <textarea
                      v-model="observationsDraft"
                      class="form-control form-control-sm"
                      rows="3"
                      :placeholder="$t('financialSummary.stages.observationsPlaceholder')"
                      @input="onObservationsInput"
                      @blur="saveObservations"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div
                class="col-lg-8 financial-summary-stages__col2"
                :class="{ 'financial-summary-stages__col--done': stage2HeaderDone }"
              >
                <div class="p-3">
                  <div
                    class="d-flex align-items-center gap-2 mb-3 financial-summary-stages__stage-head"
                    :class="{ 'financial-summary-stages__stage-head--done': stage2HeaderDone }"
                  >
                    <span
                      class="financial-summary-stages__badge financial-summary-stages__badge--secondary"
                      :class="{ 'financial-summary-stages__badge--done': stage2HeaderDone }"
                    >2</span>
                    <span
                      class="small text-uppercase fw-bold"
                      :class="stage2HeaderDone ? 'text-success' : 'text-muted'"
                    >{{ $t("financialSummary.stages.step2Short") }}</span>
                  </div>
                  <div
                    v-if="collectionStagesVisual.stage2Shippings.length || showMissingShippingPlaceholder"
                    class="d-flex flex-wrap gap-2 w-100 financial-summary-stages__ship-stack"
                  >
                    <div
                      v-for="ship in collectionStagesVisual.stage2Shippings"
                      :key="'st-ship-' + ship.shippingId"
                      class="financial-summary-stages__ship financial-summary-stages__ship--full card"
                      :class="{
                        'financial-summary-stages__ship--complete': isShippingCollectComplete(ship),
                        'financial-summary-stages__ship--compact-stage2': isShippingStage2CompactSummary(ship),
                      }"
                    >
                      <div class="card-body p-2">
                        <div
                          class="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap financial-summary-stages__ship-title-row"
                        >
                          <div class="d-flex align-items-baseline flex-wrap gap-2 min-w-0 flex-grow-1">
                            <div class="d-flex flex-column min-w-0 flex-grow-1">
                              <a
                                href="#"
                                class="fw-semibold text-truncate small text-body financial-summary-stages__ship-name-link min-w-0"
                                :title="(ship.name || '—') + ' — ' + (stage2ShippingNameOpensOperacion ? $t('fobtrading.listOpenOperacionView') : $t('financialSummary.stages.openShippingNewTab'))"
                                @click.prevent.stop="openStage2ShippingName(ship)"
                              >
                                {{ ship.name || "—" }}
                                <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                              </a>
                              <!-- Banderas del embarque, debajo de su nombre: mismo catálogo que
                                   la columna de banderas del listado; un clic en el badge abre
                                   el popup del color. -->
                              <operation-flags-card
                                v-if="operationFlagsEnabled"
                                class="align-self-start mt-1"
                                inline
                                color-pickable
                                :categories="shippingFlagCategories(ship)"
                                :catalog="operationCategoryCatalog"
                                :list-id="operationFlagListId"
                                :disabled="isShippingFlagSaving(ship)"
                                :saving="isShippingFlagSaving(ship)"
                                @change="saveShippingFlags(ship, $event)"
                                @pick-color="openFlagColorPicker"
                                @create="createShippingFlag(ship)"
                              />
                            </div>
                            <span
                              v-if="shippingStage2SupplierDisplayName(ship)"
                              class="small text-muted text-truncate flex-shrink-0 ps-2 border-start border-secondary border-opacity-25 d-inline-flex align-items-baseline gap-1 flex-wrap"
                              style="max-width: min(42vw, 16rem)"
                              :title="
                                shippingStage2SupplierContactId(ship)
                                  ? $t('financialSummary.openContactNewTab')
                                  : $t('Supplier') + ': ' + shippingStage2SupplierDisplayName(ship)
                              "
                            >
                              <span class="text-muted flex-shrink-0">{{ $t("Supplier") }}:</span>
                              <a
                                v-if="shippingStage2SupplierContactId(ship)"
                                href="#"
                                class="financial-summary-stages__supplier-contact-link text-body fw-semibold text-decoration-none text-truncate min-w-0"
                                :title="$t('financialSummary.openContactNewTab')"
                                @click.prevent.stop="openShippingStage2SupplierContact(ship)"
                              >
                                {{ shippingStage2SupplierDisplayName(ship) }}
                                <i class="fas fa-external-link-alt ms-1 opacity-50 flex-shrink-0" style="font-size: 0.65rem" aria-hidden="true"></i>
                              </a>
                              <span v-else class="fw-semibold text-body text-truncate min-w-0">{{
                                shippingStage2SupplierDisplayName(ship)
                              }}</span>
                              <NotaComercialPendingBadge
                                v-if="shippingStage2SupplierContactId(ship)"
                                class="flex-shrink-0"
                                :contact-id="String(shippingStage2SupplierContactId(ship))"
                                :contact-name="shippingStage2SupplierDisplayName(ship)"
                              />
                              <button
                                v-if="fobNcShortcutUiAllowed && shippingStage2SupplierContactId(ship)"
                                type="button"
                                class="btn btn-sm btn-outline-secondary flex-shrink-0 py-0 px-1"
                                style="font-size: 0.7rem"
                                @click.prevent.stop="openFobNotaComercialSupplierModal(ship)"
                              >
                                <i class="fas fa-plus me-1" aria-hidden="true"></i>
                                {{ $t("financialSummary.fobNotaComercial.notaComercialButton") }}
                              </button>
                              <NotaComercialAssignButton
                                v-if="shippingStage2SupplierContactId(ship) && ship.shippingId"
                                :contact-id="String(shippingStage2SupplierContactId(ship))"
                                side="supplier"
                                target-type="shipping"
                                :target-id="String(ship.shippingId)"
                                @assigned="onNotaComercialAssigned"
                              />
                            </span>
                          </div>
                          <div
                            class="d-flex flex-column align-items-start min-w-0 ps-2 border-start border-secondary border-opacity-25"
                          >
                            <div
                              class="d-flex align-items-baseline gap-1 flex-wrap text-body financial-summary-stages__containers"
                              :title="$t('Qty containers')"
                            >
                              <i class="fas fa-cube text-secondary" aria-hidden="true"></i>
                              <span class="fw-semibold tabular-nums">{{ shippingContainerCountDisplay(ship) }}</span>
                              <span class="text-muted text-nowrap financial-summary-stages__containers-label">{{
                                $t("Qty containers")
                              }}</span>
                              <span
                                v-if="shippingContainerNumbersText(ship)"
                                class="fw-semibold financial-summary-stages__container-numbers"
                                :title="$t('financialSummary.stages.containerNumbersHint')"
                              >{{ shippingContainerNumbersText(ship) }}</span>
                            </div>
                            <!-- Productos de la factura del embarque: debajo de la cantidad de
                                 contenedores, uno por renglón, y los puertos del embarque a
                                 la derecha de los productos. -->
                            <div
                              v-if="shippingInvoiceProductNames(ship).length || shippingHasPorts(ship)"
                              class="d-flex align-items-start flex-wrap gap-2 mt-1 min-w-0"
                            >
                              <div
                                v-if="shippingInvoiceProductNames(ship).length"
                                class="d-flex flex-column align-items-start gap-1 min-w-0"
                                :title="$t('financialSummary.stages.shipInvoiceProductsHint')"
                              >
                                <span
                                  v-for="(productName, pIdx) in shippingInvoiceProductNames(ship)"
                                  :key="'ship-prod-' + ship.shippingId + '-' + pIdx"
                                  class="financial-summary-stages__ship-product text-break"
                                >{{ productName }}</span>
                              </div>
                              <div
                                v-if="shippingHasPorts(ship)"
                                class="financial-summary-stages__ship-ports d-inline-flex align-items-center gap-1 text-break"
                                :title="shippingPortsHint(ship)"
                              >
                                <i class="fas fa-anchor text-secondary" aria-hidden="true"></i>
                                <span>{{ shippingPortOfLoading(ship) || "—" }}</span>
                                <i class="fas fa-arrow-right opacity-50" aria-hidden="true"></i>
                                <span>{{ shippingPortOfDelivery(ship) || "—" }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="financial-summary-stages__ship-timeline mb-2">
                          <button
                            type="button"
                            class="financial-summary-stages__ship-timeline-step"
                            :class="{
                              'financial-summary-stages__ship-timeline-step--done':
                                shippingTimelineStepDone(ship, 'invoice'),
                              'financial-summary-stages__ship-timeline-step--clickable':
                                shippingTimelineStepClickable(ship, 'invoice'),
                            }"
                            :disabled="!shippingTimelineStepClickable(ship, 'invoice')"
                            @click.stop="openShippingTimelineStep(ship, 'invoice')"
                          >
                            <i
                              v-if="shippingTimelineStepDone(ship, 'invoice')"
                              class="fas fa-check me-1"
                              aria-hidden="true"
                            ></i>
                            1. {{ $t("financialSummary.stages.shipTimelineClientInvoice") }}{{
                              shipInvoiceTotalDisplay(ship) > 0.01
                                ? ": " + moneyPrefix + formatNumber(shipInvoiceTotalDisplay(ship))
                                : ""
                            }}
                          </button>
                          <span class="financial-summary-stages__ship-timeline-sep" aria-hidden="true"
                            ><i class="fas fa-angle-right"></i
                          ></span>
                          <button
                            type="button"
                            class="financial-summary-stages__ship-timeline-step"
                            :class="{
                              'financial-summary-stages__ship-timeline-step--done':
                                shippingTimelineStepDone(ship, 'proformSupplier'),
                              'financial-summary-stages__ship-timeline-step--clickable':
                                shippingTimelineStepClickable(ship, 'proformSupplier'),
                            }"
                            :disabled="!shippingTimelineStepClickable(ship, 'proformSupplier')"
                            @click.stop="openShippingTimelineStep(ship, 'proformSupplier')"
                          >
                            <i
                              v-if="shippingTimelineStepDone(ship, 'proformSupplier')"
                              class="fas fa-check me-1"
                              aria-hidden="true"
                            ></i>
                            2. {{ $t("financialSummary.stages.shipTimelineProformSupplier") }}{{
                              (Number(ship.total_proformship) || 0) > 0.01
                                ? ": " + moneyPrefix + formatNumber(Number(ship.total_proformship) || 0)
                                : ""
                            }}
                          </button>
                          <span class="financial-summary-stages__ship-timeline-sep" aria-hidden="true"
                            ><i class="fas fa-angle-right"></i
                          ></span>
                          <button
                            type="button"
                            class="financial-summary-stages__ship-timeline-step"
                            :class="{
                              'financial-summary-stages__ship-timeline-step--done':
                                shippingTimelineStepDone(ship, 'packingList'),
                              'financial-summary-stages__ship-timeline-step--clickable':
                                shippingTimelineStepClickable(ship, 'packingList'),
                            }"
                            :disabled="!shippingTimelineStepClickable(ship, 'packingList')"
                            @click.stop="openShippingTimelineStep(ship, 'packingList')"
                          >
                            <i
                              v-if="shippingTimelineStepDone(ship, 'packingList')"
                              class="fas fa-check me-1"
                              aria-hidden="true"
                            ></i>
                            3. {{ $t("financialSummary.stages.shipTimelinePackingList") }}
                          </button>
                          <span class="financial-summary-stages__ship-timeline-sep" aria-hidden="true"
                            ><i class="fas fa-angle-right"></i
                          ></span>
                          <span
                            class="financial-summary-stages__ship-timeline-step-label"
                            :class="{
                              'financial-summary-stages__ship-timeline-step-label--done':
                                shippingTimelineStepDone(ship, 'invoiceSupplier'),
                            }"
                          >
                            <i
                              v-if="shippingTimelineStepDone(ship, 'invoiceSupplier')"
                              class="fas fa-check me-1"
                              aria-hidden="true"
                            ></i>
                            4. {{ $t("financialSummary.stages.shipTimelineInvoiceSupplier") }}{{
                              (Number(ship.total_invoicesupplier) || 0) > 0.01
                                ? ": " + moneyPrefix + formatNumber(Number(ship.total_invoicesupplier) || 0)
                                : ""
                            }}
                          </span>
                        </div>

                        <div
                          v-if="isShippingStage2CompactSummary(ship)"
                          class="financial-summary-stages__ship-compact-summary mt-1"
                          role="button"
                          tabindex="0"
                          :aria-expanded="isShippingStage2CompactExpanded(ship) ? 'true' : 'false'"
                          :aria-label="
                            isShippingStage2CompactExpanded(ship)
                              ? $t('financialSummary.stages.shipStage2CompactSummaryCollapseHint')
                              : $t('financialSummary.stages.shipStage2CompactSummaryExpandHint')
                          "
                          :title="
                            isShippingStage2CompactExpanded(ship)
                              ? $t('financialSummary.stages.shipStage2CompactSummaryCollapseHint')
                              : $t('financialSummary.stages.shipStage2CompactSummaryExpandHint')
                          "
                          @click="toggleShippingStage2CompactExpanded(ship)"
                          @keydown.enter.prevent="toggleShippingStage2CompactExpanded(ship)"
                          @keydown.space.prevent="toggleShippingStage2CompactExpanded(ship)"
                        >
                          <div
                            class="financial-summary-stages__ship-compact-summary__legend small fw-semibold text-success mb-1 d-flex align-items-center justify-content-between gap-2 flex-wrap"
                          >
                            <span class="d-inline-flex align-items-center gap-1 min-w-0">
                              <i class="fas fa-check-circle flex-shrink-0" aria-hidden="true"></i>
                              <span class="text-truncate">{{
                                $t("financialSummary.stages.shipStage2PaymentsBlockTitle")
                              }}</span>
                            </span>
                            <i
                              class="fas flex-shrink-0 text-success opacity-75"
                              style="font-size: 0.75rem"
                              :class="
                                isShippingStage2CompactExpanded(ship) ? 'fa-chevron-up' : 'fa-chevron-down'
                              "
                              aria-hidden="true"
                            ></i>
                          </div>
                          <div
                            v-if="shippingOperationMarginCollectedMinusPaid(ship) != null"
                            class="d-flex flex-wrap justify-content-between align-items-baseline gap-2"
                          >
                            <span class="small text-muted mb-0">{{
                              $t("financialSummary.stages.shipStage2OperationMarginCaption")
                            }}</span>
                            <span
                              class="fs-6 fw-bold tabular-nums mb-0"
                              :class="
                                (shippingOperationMarginCollectedMinusPaid(ship) || 0) >= -0.01
                                  ? 'text-success'
                                  : 'text-danger'
                              "
                            >{{ marginMoneyPrefix }}{{ formatNumber(shippingOperationMarginCollectedMinusPaid(ship)) }}</span>
                          </div>
                        </div>

                        <div
                          v-if="!isShippingStage2CompactSummary(ship) || isShippingStage2CompactExpanded(ship)"
                          class="row g-2 g-md-0 align-items-stretch financial-summary-stages__ship-split"
                        >
                          <div class="col-12 col-md-6 financial-summary-stages__ship-collections pe-md-2">
                            <div
                              class="financial-summary-stages__ship-collect-head mb-2 pb-2 border-bottom border-light"
                            >
                              <div
                                class="financial-summary-stages__ship-section-title text-primary mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2"
                              >
                                <span class="d-flex align-items-center min-w-0">
                                  <i class="fas fa-arrow-down me-1" aria-hidden="true"></i>
                                  {{ $t("financialSummary.stages.shipStage2CollectionsBlockTitle") }}
                                </span>
                                <div class="d-flex flex-wrap align-items-center gap-1 flex-shrink-0 justify-content-end">
                                  <button
                                    v-if="fobNcShortcutUiAllowed && collectionStagesResolvedClientId"
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary flex-shrink-0 py-0 px-1"
                                    style="font-size: 0.7rem"
                                    @click.stop="openFobNotaComercialClientModalForShipping(ship)"
                                  >
                                    <i class="fas fa-plus me-1" aria-hidden="true"></i>
                                    {{ $t("financialSummary.fobNotaComercial.notaComercialButtonShipping") }}
                                  </button>
                                  <NotaComercialAssignButton
                                    v-if="collectionStagesResolvedClientId && ship.shippingId"
                                    :contact-id="String(collectionStagesResolvedClientId)"
                                    side="client"
                                    target-type="shipping"
                                    :target-id="String(ship.shippingId)"
                                    @assigned="onNotaComercialAssigned"
                                  />
                                </div>
                              </div>
                              <div class="d-flex flex-wrap align-items-end justify-content-between gap-2">
                                <div>
                                  <div class="small text-muted">{{ $t("financialSummary.balance.totalInvoice") }}</div>
                                  <div class="fs-5 fw-semibold text-body">
                                    {{ moneyPrefix }}{{ formatNumber(shipInvoiceTotalDisplay(ship)) }}
                                  </div>
                                  <!-- La NC/ND no es cobro: baja (o sube) la meta, y esa meta es el denominador de la barra. -->
                                  <div
                                    v-if="shipInvoiceNotaImpactShow(ship)"
                                    class="small text-muted d-flex flex-wrap align-items-baseline gap-1 mt-1"
                                    style="font-size: 0.72rem"
                                  >
                                    <span
                                      v-if="shipClientNotaInvoiceCreditApplied(ship) > 0.01"
                                      class="tabular-nums"
                                    >{{
                                      $t("financialSummary.stages.shipInvoiceNotesCreditLine", {
                                        amount: moneyPrefix + formatNumber(shipClientNotaInvoiceCreditApplied(ship)),
                                      })
                                    }}</span>
                                    <span
                                      v-if="shipClientNotaInvoiceDebitApplied(ship) > 0.01"
                                      class="tabular-nums"
                                    >{{
                                      $t("financialSummary.stages.shipInvoiceNotesDebitLine", {
                                        amount: moneyPrefix + formatNumber(shipClientNotaInvoiceDebitApplied(ship)),
                                      })
                                    }}</span>
                                    <span
                                      v-if="shipClientNotaProformShareCredit(ship) > 0.01"
                                      class="tabular-nums"
                                    >{{
                                      $t("financialSummary.stages.shipProformNotesCreditLine", {
                                        amount: moneyPrefix + formatNumber(shipClientNotaProformShareCredit(ship)),
                                      })
                                    }}</span>
                                    <span
                                      v-if="shipClientNotaProformShareDebit(ship) > 0.01"
                                      class="tabular-nums"
                                    >{{
                                      $t("financialSummary.stages.shipProformNotesDebitLine", {
                                        amount: moneyPrefix + formatNumber(shipClientNotaProformShareDebit(ship)),
                                      })
                                    }}</span>
                                    <span aria-hidden="true">·</span>
                                    <span class="tabular-nums fw-semibold text-body">{{
                                      $t("financialSummary.stages.shipInvoiceNetToCollect", {
                                        amount: moneyPrefix + formatNumber(shipInvoiceNetTotalDisplay(ship)),
                                      })
                                    }}</span>
                                  </div>
                                </div>
                                <div class="d-flex flex-wrap gap-2 justify-content-end">
                                  <button
                                    v-if="
                                      canEditInvoiceUnitQty &&
                                        shipInvoiceTotalDisplay(ship) <= 0.01
                                    "
                                    type="button"
                                    class="btn btn-sm btn-outline-primary flex-shrink-0"
                                    @click.stop="openInvoiceUnitQtyModal(ship)"
                                  >
                                    <i class="fas fa-weight-hanging me-1" aria-hidden="true"></i>
                                    {{ $t("financialSummary.stages.shipCollectionsEditInvoiceUnitQtyButton") }}
                                  </button>
                                  <button
                                    v-if="showAutoloadPackingListButton(ship)"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary flex-shrink-0"
                                    @click.stop="openAutoloadPackingListModal(ship)"
                                  >
                                    <i class="fas fa-boxes me-1" aria-hidden="true"></i>
                                    {{ $t("Auto load packing list") }}
                                  </button>
                                </div>
                              </div>
                              <div
                                v-if="shipInvoiceTotalDisplay(ship) <= 0.01"
                                class="alert alert-warning py-2 px-2 mt-2 mb-0 small"
                                role="alert"
                              >
                                {{ $t("financialSummary.stages.stage2InvoiceTotalMissingText") }}
                              </div>
                              <div
                                class="d-flex flex-wrap justify-content-between align-items-baseline gap-2 small mt-3 mb-1"
                              >
                                <span class="text-muted">{{
                                  $t("financialSummary.stages.shipPaymentsProgressCaption")
                                }}</span>
                                <span class="text-body fw-semibold tabular-nums">
                                  {{ moneyPrefix }}{{ formatNumber(shippingCollectionsInvoiceCaptionNumerator(ship)) }}
                                  <span class="text-muted fw-normal">/</span>
                                  {{ moneyPrefix }}{{ formatNumber(shippingCollectionsInvoiceCaptionDenom(ship)) }}
                                </span>
                              </div>
                              <div class="progress financial-summary-stages__progress mt-0 mb-0" style="height: 0.4rem">
                                <div
                                  class="progress-bar"
                                  :class="shippingStagesProgressClass(ship)"
                                  role="progressbar"
                                  :style="{ width: shippingStagesProgressWidth(ship) + '%' }"
                                  :aria-valuenow="shippingStagesProgressWidth(ship)"
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>
                            </div>
                            <div
                              class="financial-summary-stages__ship-drop-advance"
                              :class="{ 'financial-summary-stages__drop-target--active': stagesDndHighlightShipAdvance(ship) }"
                              @dragover.prevent="onAdvanceDragOverDropzone"
                              @drop.prevent="onAdvanceDropOnShipping($event, ship, 'advance')"
                            >
                        <!-- Bloque 1: adelanto de proforma en este embarque (meta vs asignado; una sola “lógica” de referencia) -->
                        <div
                          v-if="collectionStagesVisual.stage1.advancePct > 0"
                          class="financial-summary-stages__ship-section financial-summary-stages__ship-section--advance mb-2"
                          :class="{ 'financial-summary-stages__ship-section--advance-done': isShippingAdvanceShareComplete(ship) }"
                        >
                          <div
                            class="financial-summary-stages__ship-section-title"
                            :class="{ 'text-success': isShippingAdvanceShareComplete(ship) }"
                          >
                            {{ $t("financialSummary.stages.shipStage2AdvanceBlockTitle") }}
                          </div>
                          <template v-if="ship.expectedAdvanceOnShipping != null">
                            <div
                              class="small text-uppercase"
                              style="font-size: 0.58rem; letter-spacing: 0.03em"
                              :class="isShippingAdvanceShareComplete(ship) ? 'text-success' : 'text-muted'"
                            >
                              {{ $t("financialSummary.stages.shipAdvanceMetaCaption") }}
                            </div>
                            <div class="mb-2">
                              <span class="small">
                                <span
                                  class="tabular-nums"
                                  :class="
                                    isShippingAdvanceShareComplete(ship)
                                      ? 'text-success fw-semibold'
                                      : 'financial-summary-stages__advance-pct-emphasis financial-summary-stages__advance-pct-emphasis--proform'
                                  "
                                >{{ formatAdvancePct(collectionStagesVisual.stage1.advancePct) }}%</span><span
                                  class="tabular-nums"
                                  :class="isShippingAdvanceShareComplete(ship) ? 'text-success fw-semibold' : ''"
                                >{{
                                  $t("financialSummary.stages.shipExpectedAdvanceAfterPct", {
                                    amount: moneyPrefix + formatNumber(ship.expectedAdvanceOnShipping),
                                  })
                                }}</span>
                              </span>
                            </div>
                          </template>
                         
                          <div
                            v-if="(ship.advanceRows || []).length"
                            class="d-flex flex-column gap-1 w-100 align-items-stretch"
                          >
                            <div
                              v-for="ar in ship.advanceRows"
                              v-show="ar.amount > 0.005"
                              :key="'arow-' + ar._id"
                              role="button"
                              tabindex="0"
                              class="financial-summary-stages__chip--collect-row"
                              :draggable="canCollect"
                              @dragstart="
                                onAdvanceDragStart($event, {
                                  kind: 'ship_advance',
                                  collectId: ar.collectId,
                                  fromAppliedToId: ar._id,
                                  maxAmount: ar.amount,
                                  fromShippingId: ship.shippingId,
                                })
                              "
                              @dragend="onAdvanceDragEnd"
                            >
                              <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                <i
                                  v-if="canCollect"
                                  class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                  style="font-size: 0.65rem"
                                  aria-hidden="true"
                                ></i>
                                <i
                                  v-if="canCollect"
                                  class="fas fa-arrow-left text-info flex-shrink-0 financial-summary-stages__chip-row-icon"
                                  style="font-size: 0.7rem; cursor: pointer"
                                  role="button"
                                  tabindex="0"
                                  :title="$t('financialSummary.stages.sendRowToProformHint')"
                                  aria-hidden="true"
                                  @click.stop.prevent="sendShippingCollectRowToProform(ar, ship, 'ship_advance')"
                                  @mousedown.stop
                                ></i>
                                <span class="small text-body text-break min-w-0">{{ ar.collectName || "—" }}</span>
                              </span>
                              <span class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount">
                                <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(ar.amount) }}</strong>
                                <i
                                  class="fas fa-eye text-info flex-shrink-0"
                                  style="font-size: 0.72rem"
                                  :title="$t('financialSummary.stages.openCollectNewTab')"
                                  aria-hidden="true"
                                  @click.stop.prevent="goToCollect(ar.collectId)"
                                  @mousedown.stop
                                ></i>
                                <i
                                  v-if="canCollect"
                                  class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                  style="font-size: 0.72rem; cursor: pointer"
                                  role="button"
                                  tabindex="0"
                                  :title="$t('financialSummary.stages.quickEditCollectTitle')"
                                  aria-hidden="true"
                                  @click.stop.prevent="
                                    openQuickEditCollectRow({
                                      collectId: ar.collectId,
                                      appliedToLineId: ar._id,
                                      lineAmount: ar.amount,
                                    })
                                  "
                                  @mousedown.stop
                                ></i>
                              </span>
                            </div>
                          </div>
                          <div
                            v-if="
                              canCollect &&
                              collectionStagesVisual.stage1.advancePct > 0 &&
                              (ship.expectedAdvanceOnShipping == null ||
                                !isShippingAdvanceShareComplete(ship))
                            "
                            class="mt-2 pt-2 border-top border-light"
                          >
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-success w-100"
                              :title="$t('financialSummary.stages.stage2AddCollectAdvanceHint')"
                              @click.stop="openStage2ShippingCollect(ship, 'advance')"
                            >
                              <i class="fas fa-plus me-1" aria-hidden="true"></i>
                              {{ $t("financialSummary.actions.addCollection") }}
                            </button>
                          </div>
                          <div
                            class="d-flex justify-content-between align-items-baseline gap-2 flex-wrap mt-2 pt-2 financial-summary-stages__ship-section-dashed-footer"
                          >
                            <span
                              class="small fw-semibold"
                              :class="
                                isShippingAdvanceShareComplete(ship) ? 'text-success' : 'text-muted'
                              "
                            >{{ $t("financialSummary.stages.shipAdvanceAssignedLabel") }}</span>
                            <span
                              class="fs-6 fw-bold"
                              :class="isShippingAdvanceShareComplete(ship) ? 'text-success' : 'text-info'"
                            >{{ moneyPrefix }}{{ formatNumber(ship.advanceAssignedAmount) }}</span>
                          </div>
                        </div>
                        </div>

                        <div
                          class="financial-summary-stages__ship-drop-invoice"
                          :class="{ 'financial-summary-stages__drop-target--active': stagesDndHighlightShipInvoice(ship) }"
                          @dragover.prevent="onAdvanceDragOverDropzone"
                          @drop.prevent="onAdvanceDropOnShipping($event, ship, 'invoice')"
                        >
                        <!-- Bloque 2: factura embarque — mismo ritmo visual que adelanto de proforma -->
                        <div
                          class="financial-summary-stages__ship-section financial-summary-stages__ship-section--invoice mb-2"
                          :class="{
                            'financial-summary-stages__ship-section--invoice-balanced':
                              isShippingInvoicePendingComplete(ship),
                          }"
                        >
                          <div
                            class="financial-summary-stages__ship-section-title"
                            :class="{ 'text-success': isShippingInvoicePendingComplete(ship) }"
                          >
                            {{ $t("financialSummary.stages.shipStage2InvoiceBlockTitle") }}
                          </div>
                          <div
                            class="small text-uppercase mb-2"
                            style="font-size: 0.58rem; letter-spacing: 0.03em"
                            :class="
                              isShippingInvoicePendingComplete(ship) ? 'text-success' : 'text-muted'
                            "
                          >
                            {{ $t("financialSummary.stages.shipInvoiceMetaCaption") }}
                          </div>
                          <div class="financial-summary-stages__ship-expense-card__amount-rows small mb-2 pt-1">
                            <div
                              class="d-flex align-items-end justify-content-between gap-1 text-muted financial-summary-stages__ship-expense-card__amount-labels"
                            >
                              <span class="flex-fill text-center text-truncate px-1">{{
                                $t("financialSummary.stages.shipPaymentsExpenseDueShort")
                              }}</span>
                              <span class="flex-fill text-center text-truncate px-1">{{
                                $t("financialSummary.stages.shipPaymentsExpensePaidRowShort")
                              }}</span>
                              <span class="flex-fill text-center text-truncate px-1">{{
                                $t("financialSummary.stages.shipPaymentsExpensePendingShort")
                              }}</span>
                            </div>
                            <div
                              class="d-flex align-items-baseline justify-content-between gap-1 tabular-nums pt-1 financial-summary-stages__ship-expense-card__amount-values"
                            >
                              <span
                                class="flex-fill text-center text-body px-1"
                                :title="
                                  shipInvoiceNotaImpactShow(ship)
                                    ? $t('financialSummary.stages.shipInvoiceDueNetHint', {
                                      total: moneyPrefix + formatNumber(shipInvoiceTotalDisplay(ship)),
                                    })
                                    : null
                                "
                              >{{ moneyPrefix }}{{ formatNumber(shipInvoiceNetTotalDisplay(ship))
                              }}</span>
                              <span class="flex-fill text-center text-body px-1">{{ moneyPrefix }}{{ formatNumber(shippingCollectionsInvoiceCaptionNumerator(ship))
                              }}</span>
                              <span
                                class="flex-fill text-center fw-semibold px-1"
                                :class="
                                  (Number(ship.pendingToCollect) || 0) > 0.01
                                    ? 'text-danger'
                                    : 'text-success'
                                "
                              >{{ moneyPrefix }}{{ formatNumber(Math.max(0, Number(ship.pendingToCollect) || 0))
                              }}</span>
                            </div>
                            <!-- Lo pendiente ya viene neto de las NC/ND del cliente: sin este
                                 renglón no había forma de saber que una nota lo movió. -->
                            <p
                              v-if="shipInvoiceNotaImpactShow(ship)"
                              class="small text-muted mb-0 mt-1 px-1 lh-sm text-center"
                              style="font-size: 0.7rem"
                              :title="$t('financialSummary.stages.shipPendingAfterNotesHint')"
                            >
                              <span
                                :class="
                                  shipClientNotaInvoiceNetEffect(ship) < 0 ? 'text-success' : 'text-danger'
                                "
                              >{{ fobNcNotaEffectLabel(shipClientNotaInvoiceNetEffect(ship), moneyPrefix) }}</span>
                              <i class="fas fa-arrow-right mx-1" aria-hidden="true"></i>
                              <strong class="text-body">{{ moneyPrefix }}{{
                                formatNumber(Math.max(0, Number(ship.pendingToCollect) || 0))
                              }}</strong>
                              <i class="fas fa-file-invoice ms-1" aria-hidden="true"></i>
                            </p>
                            <p
                              v-if="shipInvoiceOverCollectedAmount(ship) > 0.01"
                              class="small text-warning mb-0 mt-1 px-1 lh-sm text-center"
                              style="font-size: 0.7rem"
                            >
                              {{
                                $t("financialSummary.stages.overCollectedInvoiceHint", {
                                  amount: moneyPrefix + formatNumber(shipInvoiceOverCollectedAmount(ship)),
                                })
                              }}
                            </p>
                          </div>
                          <div class="min-w-0">
                            <div
                              v-if="(ship.shippingOnlyRows || []).length"
                              class="d-flex flex-column gap-1 w-100 align-items-stretch"
                            >
                              <div
                                v-for="row in ship.shippingOnlyRows"
                                v-show="row.amount > 0.005"
                                :key="'invonly-' + row._id"
                                role="button"
                                tabindex="0"
                                class="financial-summary-stages__chip--collect-row"
                                :class="{ 'financial-summary-stages__chip-invoice-only': !canCollect }"
                                :draggable="canCollect"
                                @dragstart="
                                  onAdvanceDragStart($event, {
                                    kind: 'shipping_only',
                                    collectId: row.collectId,
                                    fromAppliedToId: row._id,
                                    maxAmount: row.amount,
                                    fromShippingId: ship.shippingId,
                                  })
                                "
                                @dragend="onAdvanceDragEnd"
                              >
                                <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                  <i
                                    v-if="canCollect"
                                    class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                    style="font-size: 0.65rem"
                                    aria-hidden="true"
                                  ></i>
                                  <i
                                    v-if="canCollect"
                                    class="fas fa-arrow-left text-info flex-shrink-0 financial-summary-stages__chip-row-icon"
                                    style="font-size: 0.7rem; cursor: pointer"
                                    role="button"
                                    tabindex="0"
                                    :title="$t('financialSummary.stages.sendRowToProformHint')"
                                    aria-hidden="true"
                                    @click.stop.prevent="sendShippingCollectRowToProform(row, ship, 'shipping_only')"
                                    @mousedown.stop
                                  ></i>
                                  <i class="fas fa-receipt text-muted flex-shrink-0 financial-summary-stages__chip-row-icon" style="font-size: 0.65rem" aria-hidden="true"></i>
                                  <span class="small text-body text-break min-w-0">{{ row.collectName || "—" }}</span>
                                </span>
                                <span class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount">
                                  <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(row.amount) }}</strong>
                                  <i
                                    class="fas fa-eye text-info flex-shrink-0"
                                    style="font-size: 0.72rem"
                                    :title="$t('financialSummary.stages.openCollectNewTab')"
                                    aria-hidden="true"
                                    @click.stop.prevent="goToCollect(row.collectId)"
                                    @mousedown.stop
                                  ></i>
                                  <i
                                    v-if="canCollect"
                                    class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                    style="font-size: 0.72rem; cursor: pointer"
                                    role="button"
                                    tabindex="0"
                                    :title="$t('financialSummary.stages.quickEditCollectTitle')"
                                    aria-hidden="true"
                                    @click.stop.prevent="
                                      openQuickEditCollectRow({
                                        collectId: row.collectId,
                                        appliedToLineId: row._id,
                                        lineAmount: row.amount,
                                      })
                                    "
                                    @mousedown.stop
                                  ></i>
                                </span>
                              </div>
                            </div>
                          </div>
                          <template v-if="shipInvoiceNotaImpactShow(ship)">
                            <div
                              v-if="shipClientNotaInvoiceCreditApplied(ship) > 0.01"
                              class="mt-2 pt-2 border-top border-light"
                            >
                              <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                                {{ $t("financialSummary.stages.notaImpactInvoiceCreditRowTitle") }}
                              </div>
                              <div
                                v-if="fobNcLinkedClientNotesOnShippingCreditOnly(ship).length"
                                class="d-flex flex-column gap-1 w-100 align-items-stretch"
                              >
                                <a
                                  v-for="n in fobNcLinkedClientNotesOnShippingCreditOnly(ship)"
                                  :key="'fob-nc-inv-cr-' + ship.shippingId + '-' + n._id"
                                  :href="fobNcNotaComercialViewHref(n._id)"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                                  :class="{
                                    'financial-summary-stages__nota-shipping-drag':
                                      fobNcShortcutUiAllowed && !redistributeInProgress,
                                  }"
                                  :draggable="fobNcShortcutUiAllowed && !redistributeInProgress"
                                  :title="fobNcLinkedNoteTitleAttr(n)"
                                  @dragstart.stop="onNotaShippingDragStart($event, n, ship)"
                                  @dragend="onNotaProformDragEnd"
                                >
                                  <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                    <i
                                      v-if="fobNcShortcutUiAllowed && !redistributeInProgress"
                                      class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                      style="font-size: 0.65rem"
                                      aria-hidden="true"
                                    ></i>
                                    <i
                                      class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                      style="font-size: 0.65rem"
                                      aria-hidden="true"
                                    ></i>
                                    <span class="small text-body text-break min-w-0">{{
                                      fobNcLinkedNoteDisplayName(n, 80)
                                    }}</span>
                                  </span>
                                  <span
                                    class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                                  >
                                    <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(n.appliedTotal) }}</strong>
                                    <i
                                      v-if="fobNcShortcutUiAllowed"
                                      class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                      style="font-size: 0.72rem; cursor: pointer"
                                      role="button"
                                      tabindex="0"
                                      :title="$t('notaComercial.unapply.iconTitle')"
                                      @click.stop.prevent="
                                        fobNcUnapplyNote(n, {
                                          targetType: 'shipping',
                                          targetId: String(ship.shippingId),
                                          operationName: ship.name,
                                        })
                                      "
                                    ></i>
                                    <i
                                      class="fas fa-eye text-info flex-shrink-0"
                                      style="font-size: 0.72rem"
                                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                      aria-hidden="true"
                                    ></i>
                                    <i
                                      v-if="fobNcShortcutUiAllowed && fobNcLinkedNoteIsCreditNote(n)"
                                      class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                      style="font-size: 0.72rem; cursor: pointer"
                                      role="button"
                                      tabindex="0"
                                      :title="$t('financialSummary.fobNotaComercial.editCommercialNote')"
                                      aria-hidden="true"
                                      @click.stop.prevent="
                                        openFobNcEditModal(n, { mode: 'shipping', shippingId: ship.shippingId })
                                      "
                                      @mousedown.stop
                                    ></i>
                                  </span>
                                </a>
                              </div>
                              <div v-else class="small text-muted">
                                {{
                                  $t("financialSummary.stages.notaImpactInvoiceCredit", {
                                    amount: moneyPrefix + formatNumber(shipClientNotaInvoiceCreditApplied(ship)),
                                  })
                                }}
                              </div>
                            </div>
                            <div
                              v-if="shipClientNotaInvoiceDebitApplied(ship) > 0.01"
                              class="mt-2 pt-2 border-top border-light"
                            >
                              <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                                {{ $t("financialSummary.stages.notaImpactInvoiceDebitRowTitle") }}
                              </div>
                              <div
                                v-if="fobNcLinkedClientNotesOnShippingDebitOnly(ship).length"
                                class="d-flex flex-column gap-1 w-100 align-items-stretch"
                              >
                                <a
                                  v-for="n in fobNcLinkedClientNotesOnShippingDebitOnly(ship)"
                                  :key="'fob-nc-inv-db-' + ship.shippingId + '-' + n._id"
                                  :href="fobNcNotaComercialViewHref(n._id)"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                                  :class="{
                                    'financial-summary-stages__nota-shipping-drag':
                                      fobNcShortcutUiAllowed && !redistributeInProgress,
                                  }"
                                  :draggable="fobNcShortcutUiAllowed && !redistributeInProgress"
                                  :title="fobNcLinkedNoteTitleAttr(n)"
                                  @dragstart.stop="onNotaShippingDragStart($event, n, ship)"
                                  @dragend="onNotaProformDragEnd"
                                >
                                  <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                    <i
                                      v-if="fobNcShortcutUiAllowed && !redistributeInProgress"
                                      class="fas fa-grip-vertical text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                      style="font-size: 0.65rem"
                                      aria-hidden="true"
                                    ></i>
                                    <i
                                      class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                      style="font-size: 0.65rem"
                                      aria-hidden="true"
                                    ></i>
                                    <span class="small text-body text-break min-w-0">{{
                                      fobNcLinkedNoteDisplayName(n, 80)
                                    }}</span>
                                  </span>
                                  <span
                                    class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                                  >
                                    <strong class="small tabular-nums text-body">{{ moneyPrefix }}{{ formatNumber(n.appliedTotal) }}</strong>
                                    <i
                                      v-if="fobNcShortcutUiAllowed"
                                      class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                      style="font-size: 0.72rem; cursor: pointer"
                                      role="button"
                                      tabindex="0"
                                      :title="$t('notaComercial.unapply.iconTitle')"
                                      @click.stop.prevent="
                                        fobNcUnapplyNote(n, {
                                          targetType: 'shipping',
                                          targetId: String(ship.shippingId),
                                          operationName: ship.name,
                                        })
                                      "
                                    ></i>
                                    <i
                                      class="fas fa-eye text-info flex-shrink-0"
                                      style="font-size: 0.72rem"
                                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                      aria-hidden="true"
                                    ></i>
                                  </span>
                                </a>
                              </div>
                              <div v-else class="small text-muted">
                                {{
                                  $t("financialSummary.stages.notaImpactInvoiceDebit", {
                                    amount: moneyPrefix + formatNumber(shipClientNotaInvoiceDebitApplied(ship)),
                                  })
                                }}
                              </div>
                            </div>
                          </template>
                          <div
                            v-if="canCollect && !isShippingInvoicePendingComplete(ship)"
                            class="mt-2 pt-2 border-top border-light"
                          >
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-success w-100"
                              :title="$t('financialSummary.stages.stage2AddCollectInvoiceHint')"
                              @click.stop="openStage2ShippingCollect(ship, 'invoice')"
                            >
                              <i class="fas fa-plus me-1" aria-hidden="true"></i>
                              {{ $t("financialSummary.actions.addCollection") }}
                            </button>
                          </div>
                          <div
                            class="d-flex justify-content-between align-items-baseline gap-2 flex-wrap mt-2 pt-2 financial-summary-stages__ship-section-dashed-footer"
                          >
                            <span
                              class="small fw-semibold"
                              :class="
                                isShippingInvoicePendingComplete(ship)
                                  ? 'text-success'
                                  : 'text-muted'
                              "
                            >{{ $t("financialSummary.stages.shipInvoiceCollectedCaption") }}</span>
                            <span
                              class="fs-6 fw-bold"
                              :class="
                                isShippingInvoicePendingComplete(ship)
                                  ? 'text-success'
                                  : 'text-info'
                              "
                            >{{ moneyPrefix }}{{ formatNumber(shipInvoiceOnlyCollectedDisplay(ship))
                            }}</span>
                          </div>
                        </div>
                        </div>
                          </div>
                          <div class="col-12 col-md-6 financial-summary-stages__ship-payments ps-md-2">
                            <div
                              class="financial-summary-stages__ship-section financial-summary-stages__ship-section--payments h-100"
                              :class="{
                                'financial-summary-stages__ship-section--payments-complete':
                                  isShippingPaymentsBlockComplete(ship),
                              }"
                            >
                              <div
                                class="financial-summary-stages__ship-section-title"
                                :class="isShippingPaymentsBlockComplete(ship) ? 'text-success' : 'text-danger'"
                              >
                                <i class="fas fa-arrow-up me-1" aria-hidden="true"></i>
                                {{ $t("financialSummary.stages.shipStage2PaymentsBlockTitle") }}
                              </div>
                              <div
                                v-if="
                                  canMakePayments &&
                                    supplierAdvanceDraftKey(ship) &&
                                    !supplierAdvancePctDisplay(ship)
                                "
                                class="d-flex flex-wrap align-items-center gap-2 mb-3 pb-2 border-bottom border-light"
                              >
                                <label
                                  class="form-label small mb-0 flex-shrink-0"
                                  :for="'supplier-advance-sp-' + ship.shippingId"
                                >{{ $t("financialSummary.stages.supplierAdvancePctInlineLabel") }}</label>
                                <div class="input-group input-group-sm flex-grow-1" style="min-width: 7rem; max-width: 11rem">
                                  <input
                                    :id="'supplier-advance-sp-' + ship.shippingId"
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                    class="form-control"
                                    :value="supplierAdvanceDraftValue(ship)"
                                    :placeholder="
                                      supplierAdvancePctDisplay(ship)
                                        ? String(supplierAdvancePctDisplay(ship))
                                        : $t('financialSummary.stages.advancePctPlaceholder')
                                    "
                                    :disabled="savingSupplierAdvanceShippingId === String(ship.shippingId)"
                                    @input="onSupplierAdvanceDraftInput(ship, $event)"
                                    @keyup.enter="saveInlineSupplierAdvancePct(ship)"
                                  />
                                  <span class="input-group-text">%</span>
                                </div>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-primary"
                                  :disabled="savingSupplierAdvanceShippingId === String(ship.shippingId)"
                                  @click="saveInlineSupplierAdvancePct(ship)"
                                >
                                  <i
                                    v-if="savingSupplierAdvanceShippingId === String(ship.shippingId)"
                                    class="fas fa-spinner fa-spin me-1"
                                    aria-hidden="true"
                                  ></i>
                                  {{
                                    savingSupplierAdvanceShippingId === String(ship.shippingId)
                                      ? $t("financialSummary.stages.supplierAdvancePctSaving")
                                      : $t("financialSummary.stages.supplierAdvancePctSave")
                                  }}
                                </button>
                              </div>
                              <div
                                v-else-if="supplierAdvancePctDisplay(ship) && !canMakePayments"
                                class="small text-muted mb-2"
                              >
                                {{ $t("financialSummary.stages.supplierAdvancePctReadOnlyBefore") }}<span
                                  class="financial-summary-stages__advance-pct-emphasis financial-summary-stages__advance-pct-emphasis--supplier"
                                >{{ supplierAdvancePctDisplay(ship) }}%</span>
                              </div>
                              <template v-if="balanceShippingRowForStage2(ship)">
                                <div class="financial-summary-stages__ship-payments-invoice-total mb-2  border-bottom border-light">
                                  <div class="d-flex flex-wrap align-items-end justify-content-between gap-2">
                                    <div>
                                      <div class="small text-muted">
                                        {{ $t("financialSummary.stages.shipPaymentsTotalCosts") }}
                                      </div>
                                      <div class="fs-5 fw-semibold text-body">
                                        {{ shippingPaymentsPrefix(ship) }}{{ formatNumber(shippingPaymentsTotalCostsAmount(ship)) }}
                                      </div>
                                    </div>
                                    <div class="d-flex flex-wrap gap-2 align-items-end justify-content-end">
                                      <button
                                        v-if="
                                          canEditShipperPrices &&
                                            (Number(balanceShippingRowForStage2(ship).supplierInvoice) || 0) <=
                                              0.01 &&
                                            !ship.shipperUnitPricesComplete &&
                                            ship.shipperInvoiceQuantitiesComplete !== true
                                        "
                                        type="button"
                                        class="btn btn-sm btn-outline-danger flex-shrink-0"
                                        @click.stop="openShipperUnitPricesModal(ship)"
                                      >
                                        <i class="fas fa-edit me-1" aria-hidden="true"></i>
                                        {{ $t("financialSummary.stages.shipPaymentsEditShipperPricesButton") }}
                                      </button>
                                      <button
                                        v-if="
                                          supplierAdvancePctDisplay(ship) &&
                                            canEditShipperPrices &&
                                            (!ship.shipperInvoiceQuantitiesComplete ||
                                              ship.shipperUnitPricesComplete !== true) &&
                                            (ship.total_invoicesupplier === null ||
                                              ship.total_invoicesupplier === undefined ||
                                              ship.total_invoicesupplier === '' ||
                                              Number(ship.total_invoicesupplier) <= 0.01)
                                        "
                                        type="button"
                                        class="btn btn-sm btn-outline-primary flex-shrink-0"
                                        @click.stop="openShipperInvoiceUnitQtyModal(ship)"
                                      >
                                        <i
                                          :class="[
                                            'me-1',
                                            'fas',
                                            ship.shipperInvoiceQuantitiesComplete === true
                                              ? 'fa-dollar-sign'
                                              : 'fa-weight-hanging',
                                          ]"
                                          aria-hidden="true"
                                        ></i>
                                        {{
                                          ship.shipperInvoiceQuantitiesComplete === true
                                            ? $t("financialSummary.stages.shipPaymentsEditShipperPricesButton")
                                            : $t("financialSummary.stages.shipPaymentsEditShipperInvoiceQtyButton")
                                        }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  v-if="
                                    supplierAdvancePctDisplay(ship) &&
                                      (ship.total_invoicesupplier === null ||
                                        ship.total_invoicesupplier === undefined ||
                                        ship.total_invoicesupplier === '' ||
                                        Number(ship.total_invoicesupplier) <= 0.01) &&
                                      (ship.total_proformship === null ||
                                        ship.total_proformship === undefined ||
                                        ship.total_proformship === '' ||
                                        Number(ship.total_proformship) <= 0.01)
                                  "
                                  class="alert alert-warning py-2 px-2 mt-2 mb-0 small"
                                  role="alert"
                                >
                                  {{
                                    $t("financialSummary.stages.shipPaymentsSupplierInvoiceTotalMissingText")
                                  }}
                                </div>
                                <div class="financial-summary-stages__ship-payments-progress mb-2">
                                  <div class="d-flex flex-wrap justify-content-between align-items-baseline gap-2 small mb-1">
                                    <span class="text-muted">{{
                                      $t("financialSummary.stages.shipPaymentsProgressCaption")
                                    }}</span>
                                    <span class="text-body fw-semibold tabular-nums">
                                      {{ shippingPaymentsPrefix(ship) }}{{ formatNumber(shippingPaymentsProgressApplied(ship)) }}
                                      <span class="text-muted fw-normal">/</span>
                                      {{ shippingPaymentsPrefix(ship) }}{{ formatNumber(shippingPaymentsProgressDenom(ship)) }}
                                    </span>
                                  </div>
                                  <div
                                    class="progress financial-summary-stages__progress financial-summary-stages__progress--payments-split"
                                    style="height: 0.4rem"
                                    role="group"
                                    :aria-label="$t('financialSummary.stages.shipPaymentsProgressAriaGroup')"
                                  >
                                    <div
                                      class="progress-bar bg-info"
                                      role="progressbar"
                                      :style="{ width: shippingPaymentsProgressPaidWidthPct(ship) + '%' }"
                                      :aria-valuenow="Math.round(shippingPaymentsProgressPaidWidthPct(ship))"
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                      :title="$t('financialSummary.stages.shipPaymentsProgressLegendPaid')"
                                    />
                                    <div
                                      class="progress-bar bg-warning"
                                      role="progressbar"
                                      :style="{ width: shippingPaymentsProgressRequestedWidthPct(ship) + '%' }"
                                      :aria-valuenow="Math.round(shippingPaymentsProgressRequestedWidthPct(ship))"
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                      :title="$t('financialSummary.stages.shipPaymentsProgressLegendRequested')"
                                    />
                                  </div>
                                  <div
                                    v-if="shippingPaymentsProgressDenom(ship) > 0.01"
                                    class="d-flex flex-wrap align-items-center gap-3 small text-muted mt-1 mb-0"
                                  >
                                    <span class="d-inline-flex align-items-center gap-1">
                                      <i class="fas fa-square text-info" style="font-size: 0.55rem" aria-hidden="true"></i>
                                      {{ $t("financialSummary.stages.shipPaymentsProgressLegendPaid") }}
                                      <span class="tabular-nums text-body">{{ shippingPaymentsPrefix(ship) }}{{ formatNumber(shippingPaymentsProgressPaidAmount(ship)) }}</span>
                                    </span>
                                    <span class="d-inline-flex align-items-center gap-1">
                                      <i class="fas fa-square text-warning" style="font-size: 0.55rem" aria-hidden="true"></i>
                                      {{ $t("financialSummary.stages.shipPaymentsProgressLegendRequested") }}
                                      <span class="tabular-nums text-body">{{ shippingPaymentsPrefix(ship) }}{{ formatNumber(shippingPaymentsProgressRequestedPendingAmount(ship)) }}</span>
                                    </span>
                                  </div>
                                </div>
                                <div
                                  v-if="shippingPaymentExpenseLines(ship).length"
                                  class="financial-summary-stages__ship-payments-expenses mt-2 pt-2 border-top border-light"
                                >
                                  <div class="small text-uppercase text-muted mb-2" style="font-size: 0.58rem; letter-spacing: 0.03em">
                                    {{ $t("financialSummary.stages.shipPaymentsExpensesCaption") }}
                                  </div>
                                  <div class="financial-summary-stages__ship-expense-cards d-flex flex-wrap gap-2">
                                    <div
                                      v-for="line in shippingPaymentExpenseLines(ship)"
                                      :key="line.key"
                                      class="financial-summary-stages__ship-expense-card small"
                                      :class="{
                                        'financial-summary-stages__ship-expense-card--fully-paid':
                                          shippingPaymentExpenseLineFullyPaid(line),
                                        'financial-summary-stages__ship-expense-card--in-trash':
                                          line.key === 'supplier' && line.expenseInTrash,
                                        'financial-summary-stages__ship-expense-card--paired':
                                          line.pairedWithSplits,
                                      }"
                                    >
                                      <div class="financial-summary-stages__ship-expense-card__head py-2 px-2">
                                        <div
                                          class="mb-2 d-flex align-items-start justify-content-between gap-2"
                                          :class="{ 'flex-column gap-1': line.pairedWithSplits }"
                                        >
                                          <div
                                            class="flex-grow-1 d-flex align-items-start gap-1 min-w-0"
                                            style="min-width: 5rem"
                                          >
                                            <span
                                              class="fw-semibold text-truncate"
                                              :class="
                                                shippingPaymentExpenseLineFullyPaid(line)
                                                  ? 'text-success'
                                                  : 'text-body'
                                              "
                                            >{{
                                              shippingPaymentExpenseLineLabel(line)
                                            }}</span>
                                            <span
                                              v-if="line.key === 'supplier' && line.expenseInTrash"
                                              class="financial-summary-stages__ship-expense-in-trash-badge d-inline-flex align-items-center gap-1 text-warning flex-shrink-0 ms-1"
                                              role="status"
                                              :title="$t('financialSummary.stages.shipPaymentsSupplierExpenseTrashHint')"
                                              :aria-label="$t('financialSummary.stages.shipPaymentsSupplierExpenseTrashHint')"
                                            >
                                              <i class="fas fa-trash-alt" aria-hidden="true"></i>
                                              <span class="financial-summary-stages__ship-expense-in-trash-badge__text">{{
                                                $t("financialSummary.stages.shipPaymentsSupplierExpenseInTrashLabel")
                                              }}</span>
                                            </span>
                                            <button
                                              v-if="line.viewExpenseId"
                                              type="button"
                                              class="btn btn-link btn-sm p-0 flex-shrink-0 text-primary"
                                              :title="$t('financialSummary.stages.shipPaymentsViewShippingExpense')"
                                              :aria-label="$t('financialSummary.stages.shipPaymentsViewShippingExpense')"
                                              @click.stop="openShippingExpenseDynamicView(line)"
                                            >
                                              <i class="fas fa-eye" aria-hidden="true"></i>
                                            </button>
                                            <button
                                              v-if="canMakePayments && line.expenseId"
                                              type="button"
                                              class="btn btn-link btn-sm p-0 flex-shrink-0 text-info"
                                              :title="$t('financialSummary.stages.editShippingExpense')"
                                              :aria-label="$t('financialSummary.stages.editShippingExpense')"
                                              @click.stop="openEditShippingExpense(ship, line)"
                                            >
                                              <i class="fas fa-pencil-alt" aria-hidden="true"></i>
                                            </button>
                                          </div>
                                          <div
                                            v-if="shippingPaymentExpenseLineContactName(line)"
                                            class="small text-muted d-inline-flex align-items-center gap-1 flex-shrink-0 text-nowrap"
                                            style="font-size: 0.72rem"
                                          >
                                            <i class="fas fa-user" aria-hidden="true"></i>
                                            <button
                                              v-if="shippingPaymentExpenseLineContactId(line)"
                                              type="button"
                                              class="btn btn-link btn-sm p-0 text-decoration-none text-body align-baseline"
                                              :title="shippingPaymentExpenseLineContactName(line)"
                                              @click.stop.prevent="
                                                goToContactSameTab(shippingPaymentExpenseLineContactId(line))
                                              "
                                            >
                                              {{ shippingPaymentExpenseLineContactName(line) }}
                                            </button>
                                            <button
                                              v-if="shippingPaymentExpenseLineContactId(line)"
                                              type="button"
                                              class="btn btn-link btn-sm p-0 text-decoration-none text-muted align-baseline"
                                              :title="$t('financialSummary.openContactNewTab')"
                                              @click.stop.prevent="
                                                goToContact(shippingPaymentExpenseLineContactId(line))
                                              "
                                            >
                                              <i
                                                class="fas fa-external-link-alt"
                                                style="font-size: 0.66rem"
                                                aria-hidden="true"
                                              ></i>
                                            </button>
                                            <span v-else class="text-body">{{
                                              shippingPaymentExpenseLineContactName(line)
                                            }}</span>
                                          </div>
                                        </div>
                                        <div
                                          class="financial-summary-stages__ship-expense-card__amount-rows small pt-1"
                                        >
                                          <!-- A la par de sus tramos la tarjeta va a medio ancho: un monto por renglón. -->
                                          <div
                                            v-if="line.pairedWithSplits"
                                            class="tabular-nums px-1"
                                          >
                                            <div class="d-flex justify-content-between gap-2">
                                              <span class="text-muted">{{
                                                $t("financialSummary.stages.shipPaymentsExpenseDueShort")
                                              }}</span>
                                              <span class="text-body">{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(line.amount) }}</span>
                                            </div>
                                            <div class="d-flex justify-content-between gap-2">
                                              <span class="text-muted">{{
                                                $t("financialSummary.stages.shipPaymentsExpensePaidRowShort")
                                              }}</span>
                                              <span class="text-body">{{ shippingPaymentLinePrefix(line) }}{{
                                                formatNumber(shippingPaymentLineCommitted(line))
                                              }}</span>
                                            </div>
                                            <div class="d-flex justify-content-between gap-2">
                                              <span class="text-muted">{{
                                                $t("financialSummary.stages.shipPaymentsExpensePendingShort")
                                              }}</span>
                                              <span
                                                class="fw-semibold"
                                                :class="
                                                  shippingPaymentLinePending(line) > 0.01
                                                    ? 'text-danger'
                                                    : 'text-success'
                                                "
                                              >{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(shippingPaymentLinePending(line)) }}</span>
                                            </div>
                                          </div>
                                          <div
                                            v-if="!line.pairedWithSplits"
                                            class="d-flex align-items-end justify-content-between gap-1 text-muted financial-summary-stages__ship-expense-card__amount-labels"
                                          >
                                            <span class="flex-fill text-center text-truncate px-1">{{
                                              $t("financialSummary.stages.shipPaymentsExpenseDueShort")
                                            }}</span>
                                            <span class="flex-fill text-center text-truncate px-1">{{
                                              $t("financialSummary.stages.shipPaymentsExpensePaidRowShort")
                                            }}</span>
                                            <span class="flex-fill text-center text-truncate px-1">{{
                                              $t("financialSummary.stages.shipPaymentsExpensePendingShort")
                                            }}</span>
                                          </div>
                                          <div
                                            v-if="!line.pairedWithSplits"
                                            class="d-flex align-items-baseline justify-content-between gap-1 tabular-nums pt-1 financial-summary-stages__ship-expense-card__amount-values"
                                          >
                                            <span class="flex-fill text-center text-body px-1">{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(line.amount)
                                            }}</span>
                                            <span class="flex-fill text-center text-body px-1">{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(shippingPaymentLineCommitted(line))
                                            }}</span>
                                            <span
                                              class="flex-fill text-center fw-semibold px-1"
                                              :class="
                                                shippingPaymentLinePending(line) > 0.01
                                                  ? 'text-danger'
                                                  : 'text-success'
                                              "
                                            >{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(shippingPaymentLinePending(line)) }}</span>
                                          </div>
                                          <!-- Lo pendiente ya viene neto de las notas del gasto: sin este
                                               renglón no había forma de saber que una nota lo movió. -->
                                          <p
                                            v-if="Math.abs(fobNcLineNotaNetEffect(ship, line)) > 0.01"
                                            class="small text-muted mb-0 mt-1 px-1 lh-sm text-center"
                                            style="font-size: 0.7rem"
                                            :title="$t('financialSummary.stages.shipPendingAfterNotesHint')"
                                          >
                                            <span
                                              :class="
                                                fobNcLineNotaNetEffect(ship, line) < 0
                                                  ? 'text-success'
                                                  : 'text-danger'
                                              "
                                            >{{ fobNcNotaEffectLabel(fobNcLineNotaNetEffect(ship, line), moneyPrefix) }}</span>
                                            <i class="fas fa-arrow-right mx-1" aria-hidden="true"></i>
                                            <strong class="text-body">{{ shippingPaymentLinePrefix(line) }}{{
                                              formatNumber(shippingPaymentLinePending(line))
                                            }}</strong>
                                            <i class="fas fa-file-invoice ms-1" aria-hidden="true"></i>
                                          </p>
                                          <!-- Mismo pago aplicado más de una vez: «Pag./ped.» queda inflado y
                                               las pastillas de abajo siguen mostrando un solo pago. -->
                                          <p
                                            v-if="shippingPaymentLineHasDuplicatedApplied(line)"
                                            class="small text-danger fw-semibold mb-0 mt-1 px-1 lh-sm"
                                            style="font-size: 0.7rem"
                                          >
                                            <i class="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
                                            {{
                                              $t("financialSummary.stages.shipPaymentsExpenseDuplicatedAppliedHint", {
                                                amount:
                                                  shippingPaymentLinePrefix(line) + formatNumber(shippingPaymentLineDuplicatedApplied(line)),
                                              })
                                            }}
                                          </p>
                                          <p
                                            v-if="
                                              isDuntowerTenant &&
                                              shippingPaymentLineCoveredAwaitingBank(line)
                                            "
                                            class="small text-muted mb-0 mt-1 px-1 lh-sm"
                                            style="font-size: 0.7rem"
                                          >
                                            {{
                                              $t(
                                                "financialSummary.stages.shipPaymentsExpenseCoveredAwaitingBankHint"
                                              )
                                            }}
                                          </p>
                                        </div>
                                      </div>
                                      <div
                                        v-for="adv in supplierInvoiceAdvanceSummaryLineRichList(line, ship)"
                                        :key="'advsum-' + ship.shippingId + '-' + line.key + '-' + adv.pct"
                                        class="px-2 pb-2 small text-body lh-sm financial-summary-stages__stage1-advance-line border-bottom border-light"
                                        style="font-size: 0.82rem"
                                      >
                                        <span class="fw-semibold tabular-nums">
                                          <span
                                            class="financial-summary-stages__advance-pct-emphasis financial-summary-stages__advance-pct-emphasis--supplier"
                                          >{{ adv.pct }}%</span><span>{{ adv.tail }}</span>
                                        </span>
                                      </div>
                                      <div
                                        v-if="line.linkedPayments && line.linkedPayments.length"
                                        class="financial-summary-stages__ship-expense-card__payments px-2 pb-2"
                                      >
                                        <div
                                          class="text-uppercase text-muted mb-1 pt-1 border-top border-light"
                                          style="font-size: 0.55rem; letter-spacing: 0.04em"
                                        >
                                          {{ $t("financialSummary.stages.shipPaymentsLinkedForExpenseCaption") }}
                                        </div>
                                        <div class="d-flex flex-wrap gap-1 align-items-stretch financial-summary-stages__chips">
                                          <div
                                            v-for="pmt in line.linkedPayments"
                                            :key="'paypill-' + line.key + '-' + pmt._id"
                                            class="financial-summary-stages__chip financial-summary-stages__chip--payment border rounded-2 px-2 py-1 text-start d-inline-flex align-items-center flex-wrap gap-1 shadow-sm"
                                            :class="
                                              expensePaymentPillIsPaid(pmt)
                                                ? 'financial-summary-stages__chip--payment-paid bg-light'
                                                : 'bg-white'
                                            "
                                          >
                                            <i
                                              v-if="expensePaymentPillEstimatedClockClass(pmt)"
                                              class="far fa-clock flex-shrink-0 align-self-center"
                                              :class="expensePaymentPillEstimatedClockClass(pmt)"
                                              style="font-size: 0.72rem"
                                              aria-hidden="true"
                                            ></i>
                                           
                                            <i v-if="expensePaymentPillIsPaid(pmt)"
                                             
                                              class="fas fa-check-circle text-success flex-shrink-0"
                                              style="font-size: 0.72rem"
                                              aria-hidden="true"
                                            ></i>
                                            <span class="d-block text-truncate" style="max-width: 6.5rem">{{
                                              (pmt.name && String(pmt.name).trim()) || "—"
                                            }}</span>
                                            <span
                                              v-if="expensePaymentPillIsPaid(pmt)"
                                              class="badge bg-success flex-shrink-0"
                                              style="font-size: 0.6rem"
                                            >{{ $t("financialSummary.stages.shipPaymentsPillPaidBadge") }}</span>
                                            <span
                                              v-if="expensePaymentPillTimesApplied(pmt) > 1"
                                              class="badge bg-danger flex-shrink-0"
                                              style="font-size: 0.6rem"
                                              :title="
                                                $t('financialSummary.stages.shipPaymentsPillDuplicatedBadgeHint', {
                                                  times: expensePaymentPillTimesApplied(pmt),
                                                })
                                              "
                                            >×{{ expensePaymentPillTimesApplied(pmt) }}</span>
                                            <strong
                                              class="small flex-shrink-0"
                                              :class="expensePaymentPillIsPaid(pmt) ? 'text-success' : 'text-danger'"
                                            >{{ currencyPrefixFor(pmt.currencyId || line.currencyId) }}{{ formatNumber(pmt.amount) }}</strong>
                                            <button
                                              v-if="canFinanceManagePayments && !expensePaymentPillIsPaid(pmt)"
                                              type="button"
                                              class="btn btn-danger btn-sm py-0 px-2 ms-1 flex-shrink-0"
                                              :title="$t('financialSummary.stages.shipPaymentsQuickPayHint')"
                                              @click.stop="openQuickMarkPaymentPaidModal(pmt)"
                                            >
                                              {{ $t("financialSummary.stages.shipPaymentsQuickPayButton") }}
                                            </button>
                                            <i
                                              class="fas fa-eye text-info flex-shrink-0 ms-1"
                                              style="font-size: 0.75rem; cursor: pointer"
                                              :title="$t('View payment')"
                                              aria-hidden="true"
                                              role="button"
                                              tabindex="0"
                                              @click.stop.prevent="goToPayment(pmt)"
                                              @mousedown.stop
                                              @keydown.enter.prevent="goToPayment(pmt)"
                                            ></i>
                                            <i
                                              class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                              style="font-size: 0.75rem"
                                              :title="$t('financialSummary.viewDetails')"
                                              aria-hidden="true"
                                              role="button"
                                              tabindex="0"
                                              @click.stop.prevent="openQuickEditPaymentModal(pmt)"
                                              @mousedown.stop
                                            ></i>
                                            <i
                                              v-if="canDeletePayments"
                                              class="fas fa-trash-alt text-danger flex-shrink-0 ms-1"
                                              style="font-size: 0.75rem; cursor: pointer"
                                              :title="$t('financialSummary.stages.shipPaymentsDeletePaymentHint')"
                                              aria-hidden="true"
                                              role="button"
                                              tabindex="0"
                                              @click.stop.prevent="deleteLinkedPayment(pmt)"
                                              @mousedown.stop
                                              @keydown.enter.prevent="deleteLinkedPayment(pmt)"
                                            ></i>
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        v-if="canMakePayments && shippingPaymentLineCanRequest(line)"
                                        class="financial-summary-stages__ship-expense-card__request-footer px-2 pb-2 pt-2 border-top border-light"
                                      >
                                        <button
                                          type="button"
                                          class="btn btn-sm btn-outline-danger w-100"
                                          @click.stop="requestShippingExpensePayment(ship, line)"
                                        >
                                          <i class="fas fa-file-invoice-dollar me-1" aria-hidden="true"></i>
                                          {{
                                            expensesPaidWithoutRequest
                                              ? $t("financialSummary.stages.shipPaymentsPayNowButton")
                                              : $t("financialSummary.stages.shipPaymentsRequestPaymentButton")
                                          }}
                                        </button>
                                        <button
                                          v-if="line.key === 'supplier'"
                                          type="button"
                                          class="btn btn-sm btn-outline-secondary w-100 mt-1"
                                          @click.stop="requestShippingExpenseSplitPayment(ship, line)"
                                        >
                                          <i class="fas fa-code-branch me-1" aria-hidden="true"></i>
                                          {{ $t("financialSummary.stages.shipPaymentsDividePaymentButton") }}
                                        </button>
                                      </div>
                                      <!-- Notas comerciales de este gasto: crear una nueva, asignar
                                           una que ya existe, y ver las que ya tiene ligadas. -->
                                      <div
                                        v-if="fobNcShortcutUiAllowed && line.expenseId"
                                        class="px-2 pb-2 pt-2 border-top border-light d-flex flex-wrap align-items-center gap-1"
                                      >
                                        <button
                                          type="button"
                                          class="btn btn-sm btn-outline-secondary flex-shrink-0 py-0 px-1"
                                          style="font-size: 0.7rem"
                                          @click.stop="openSupplierNoteForPaymentLine(line)"
                                        >
                                          <i class="fas fa-plus me-1" aria-hidden="true"></i>
                                          {{ $t("financialSummary.fobNotaComercial.addSupplierNote") }}
                                        </button>
                                        <NotaComercialAssignButton
                                          v-if="line.contactId && ship.shippingId"
                                          :contact-id="String(line.contactId)"
                                          side="supplier"
                                          target-type="shipping"
                                          :target-id="String(ship.shippingId)"
                                          :expense-id="String(line.expenseId || '')"
                                          @assigned="onNotaComercialAssigned"
                                        />
                                      </div>
                                      <!-- Notas de este gasto: antes solo salían las de la factura
                                           del proveedor y las del flete o el courier no aparecían. -->
                                      <template v-if="fobNcLineNotaImpactShow(ship, line)">
                                        <div class="px-2 pb-2">
                                          <div
                                            v-if="fobNcLineNotaCreditApplied(ship, line) > 0.01"
                                            class="mt-2 pt-2 border-top border-light"
                                          >
                                            <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                                              {{ $t("financialSummary.stages.notaImpactSupplierPaymentsCreditRowTitle") }}
                                            </div>
                                            <div
                                              v-if="fobNcLinePaymentNotesCreditOnly(ship, line).length"
                                              class="d-flex flex-column gap-1 w-100 align-items-stretch"
                                            >
                                              <a
                                                v-for="n in fobNcLinePaymentNotesCreditOnly(ship, line)"
                                                :key="'fob-nc-cr-' + line.key + '-' + n._id"
                                                :href="fobNcNotaComercialViewHref(n._id)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                                                :title="fobNcLinkedNoteTitleAttr(n)"
                                              >
                                                <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                                  <i
                                                    class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                                    style="font-size: 0.65rem"
                                                    aria-hidden="true"
                                                  ></i>
                                                  <span class="small text-body text-break min-w-0">{{
                                                    fobNcLinkedNoteDisplayName(n, 80)
                                                  }}</span>
                                                </span>
                                                <span
                                                  class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                                                >
                                                  <strong class="small tabular-nums text-body">{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(n.appliedTotal)
                                                  }}</strong>
                                                  <i
                                                    v-if="fobNcShortcutUiAllowed"
                                                    class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                                    style="font-size: 0.72rem; cursor: pointer"
                                                    role="button"
                                                    tabindex="0"
                                                    :title="$t('notaComercial.unapply.iconTitle')"
                                                    @click.stop.prevent="
                                                      fobNcUnapplyNote(n, {
                                                        targetType: 'shipping',
                                                        targetId: String(ship.shippingId),
                                                        operationName: ship.name,
                                                      })
                                                    "
                                                  ></i>
                                                  <i
                                                    class="fas fa-eye text-info flex-shrink-0"
                                                    style="font-size: 0.72rem"
                                                    :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                                    aria-hidden="true"
                                                  ></i>
                                                  <i
                                                    v-if="fobNcShortcutUiAllowed && fobNcLinkedNoteIsCreditNote(n)"
                                                    class="fas fa-pencil-alt text-info flex-shrink-0 ms-1"
                                                    style="font-size: 0.72rem; cursor: pointer"
                                                    role="button"
                                                    tabindex="0"
                                                    :title="$t('financialSummary.fobNotaComercial.editCommercialNote')"
                                                    aria-hidden="true"
                                                    @click.stop.prevent="
                                                      openFobNcEditModal(n, {
                                                        mode: 'shipping',
                                                        shippingId: ship.shippingId,
                                                      })
                                                    "
                                                    @mousedown.stop
                                                  ></i>
                                                </span>
                                              </a>
                                            </div>
                                            <div v-else class="small text-muted">
                                              {{
                                                $t("financialSummary.stages.notaImpactSupplierPaymentsCredit", {
                                                  amount: shippingPaymentLinePrefix(line) + formatNumber(fobNcLineNotaCreditApplied(ship, line)),
                                                })
                                              }}
                                            </div>
                                          </div>
                                          <div
                                            v-if="fobNcLineNotaDebitApplied(ship, line) > 0.01"
                                            class="mt-2 pt-2 border-top border-light"
                                          >
                                            <div class="financial-summary-stages__ship-section-title text-muted mb-1">
                                              {{ $t("financialSummary.stages.notaImpactSupplierPaymentsDebitRowTitle") }}
                                            </div>
                                            <div
                                              v-if="fobNcLinePaymentNotesDebitOnly(ship, line).length"
                                              class="d-flex flex-column gap-1 w-100 align-items-stretch"
                                            >
                                              <a
                                                v-for="n in fobNcLinePaymentNotesDebitOnly(ship, line)"
                                                :key="'fob-nc-db-' + line.key + '-' + n._id"
                                                :href="fobNcNotaComercialViewHref(n._id)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="financial-summary-stages__chip--collect-row financial-summary-stages__chip--collect-row--nota financial-summary-stages__chip-invoice-only text-decoration-none text-body"
                                                :title="fobNcLinkedNoteTitleAttr(n)"
                                              >
                                                <span class="d-flex align-items-start gap-1 min-w-0 flex-grow-1">
                                                  <i
                                                    class="fas fa-file-invoice text-muted flex-shrink-0 financial-summary-stages__chip-row-icon"
                                                    style="font-size: 0.65rem"
                                                    aria-hidden="true"
                                                  ></i>
                                                  <span class="small text-body text-break min-w-0">{{
                                                    fobNcLinkedNoteDisplayName(n, 80)
                                                  }}</span>
                                                </span>
                                                <span
                                                  class="d-flex align-items-start gap-1 flex-shrink-0 financial-summary-stages__chip-row-amount"
                                                >
                                                  <strong class="small tabular-nums text-body">{{ shippingPaymentLinePrefix(line) }}{{ formatNumber(n.appliedTotal)
                                                  }}</strong>
                                                  <i
                                                    v-if="fobNcShortcutUiAllowed"
                                                    class="fas fa-unlink text-warning flex-shrink-0 ms-1"
                                                    style="font-size: 0.72rem; cursor: pointer"
                                                    role="button"
                                                    tabindex="0"
                                                    :title="$t('notaComercial.unapply.iconTitle')"
                                                    @click.stop.prevent="
                                                      fobNcUnapplyNote(n, {
                                                        targetType: 'shipping',
                                                        targetId: String(ship.shippingId),
                                                        operationName: ship.name,
                                                      })
                                                    "
                                                  ></i>
                                                  <i
                                                    class="fas fa-eye text-info flex-shrink-0"
                                                    style="font-size: 0.72rem"
                                                    :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                                                    aria-hidden="true"
                                                  ></i>
                                                </span>
                                              </a>
                                            </div>
                                            <div v-else class="small text-muted">
                                              {{
                                                $t("financialSummary.stages.notaImpactSupplierPaymentsDebit", {
                                                  amount: shippingPaymentLinePrefix(line) + formatNumber(fobNcLineNotaDebitApplied(ship, line)),
                                                })
                                              }}
                                            </div>
                                          </div>
                                        </div>
                                      </template>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  v-if="canAutoloadExpenses || canLoadNegotiationExpenses"
                                  class="mt-2 d-flex flex-wrap gap-2"
                                >
                                  <button
                                    v-if="canAutoloadExpenses"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    @click.stop="openAutoloadExpensesModal(ship)"
                                  >
                                    <i class="fas fa-magic me-1" aria-hidden="true"></i>
                                    {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesButton") }}
                                  </button>
                                  <button
                                    v-if="canLoadNegotiationExpenses"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    :disabled="loadingNegotiationExpenses"
                                    @click.stop="$emit('load-negotiation-expenses', ship)"
                                  >
                                    <i
                                      :class="loadingNegotiationExpenses ? 'fas fa-spinner fa-spin' : 'fas fa-handshake'"
                                      class="me-1"
                                      aria-hidden="true"
                                    ></i>
                                    {{ $t("fobtrading.negotiationExpenses.button") }}
                                  </button>
                                </div>
                              </template>
                              <div v-else class="small text-muted mb-0">
                                {{ $t("financialSummary.stages.shipPaymentsNoBalance") }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="showMissingShippingPlaceholder"
                      class="financial-summary-stages__ship financial-summary-stages__ship--placeholder card w-100"
                    >
                      <div class="card-body p-2 d-flex flex-column align-items-center justify-content-center text-center">
                        <i class="fas fa-shipping-fast fa-lg text-secondary mb-2 opacity-75" aria-hidden="true"></i>
                        <div class="small fw-semibold text-secondary">
                          {{ $t("financialSummary.stages.missingShippingPlaceholderTitle") }}
                        </div>
                        <div class="w-100 mt-2">
                          <div class="small text-muted mb-1 text-center">
                            {{
                              $t("financialSummary.stages.missingShippingContainersCaption", {
                                shipped: missingShippingContainersShippedRounded,
                                total: missingShippingContainersTotalRounded,
                              })
                            }}
                          </div>
                          <div class="progress financial-summary-stages__progress" style="height: 0.4rem">
                            <div
                              class="progress-bar"
                              :class="missingShippingContainersProgressClass"
                              role="progressbar"
                              :style="{ width: missingShippingContainersProgressWidth + '%' }"
                              :aria-valuenow="missingShippingContainersProgressWidth"
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                        <div
                          class="small text-muted mt-2 financial-summary-stages__missing-shipping-body w-100"
                        >
                          {{
                            $t("financialSummary.stages.missingShippingPlaceholderBody", {
                              remaining: Math.round(containerSlotsRemaining),
                            })
                          }}
                        </div>
                        <div
                          class="d-flex flex-wrap gap-2 justify-content-center align-items-center mt-2"
                        >
                          <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            @click.stop="openAddShippingFromProform"
                          >
                            <i class="fas fa-plus me-1" aria-hidden="true"></i>
                            {{ $t("financialSummary.stages.addShippingButton") }}
                          </button>
                          <button
                            v-if="canCollect"
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click.stop="openQuickShippingShipperModal"
                          >
                            <i class="fas fa-truck me-1" aria-hidden="true"></i>
                            {{ $t("financialSummary.actions.addShippingShipper") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-muted small py-4 text-center rounded bg-light"
                  >
                    <div
                      v-if="stage2EmptyStateProformContainersZero"
                      class="alert alert-warning text-start mb-0 mx-auto"
                      style="max-width: 36rem"
                      role="status"
                    >
                      <i class="fas fa-cube me-2" aria-hidden="true"></i>
                      {{ $t("financialSummary.stages.noShippingsYetZeroProformContainers") }}
                    </div>
                    <template v-else>
                      {{ $t("financialSummary.stages.noShippingsYet") }}
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

    <!-- Add Collection Modal (simplified: bank account, comment, file) -->
    <QuickAddCollectModal
      v-if="showAddCollectionModal"
      :isVisible="showAddCollectionModal"
      :lists="listsForModals"
      :parentID="parentID"
      :parentModel="parentModel"
      :extraParams="extraParams"
      :allow-amount-edit="quickCollectAllowAmountEdit"
      :amount-max="quickCollectAmountMax"
      :stage1-auto-ship-advance-split="quickCollectStage1AutoShipSplitContext"
      :show-container-amount-calculator="showCollectContainerCalculator"
      :prefill-source-item="showCollectContainerCalculator ? itemData : null"
      :prefill-source-model="showCollectContainerCalculator ? modelName || 'Proform' : ''"
      :quick-collect-advance-pct="
        showCollectContainerCalculator &&
        collectionStagesVisual &&
        collectionStagesVisual.stage1
          ? Number(collectionStagesVisual.stage1.advancePct)
          : null
      "
      @close="closeAddCollectionModal"
      @saved="handleCollectionSaved"
    />
    <ProformAdvanceModal
      :is-visible="showProformAdvanceModal"
      :total-proform="Number(stage1DisplayTotalProform) || 0"
      :current-pct="Number(collectionStagesVisual && collectionStagesVisual.stage1 ? collectionStagesVisual.stage1.advancePct : 0) || 0"
      :current-payable="proformPayableCurrentId"
      :payable-options="proformPayableOptions"
      :saving="savingProformAdvance"
      :money-prefix="moneyPrefix"
      @close="closeProformAdvanceModal"
      @save="saveProformAdvanceConfig"
    />

    <QuickAddCollectModal
      v-if="showQuickShippingShipperModal"
      :isVisible="showQuickShippingShipperModal"
      target-model="shipping"
      :quick-field-keys="['shipper', 'qtycontainers', 'containers']"
      modal-title-key="financialSummary.quickAddShippingShipperTitle"
      modal-hint-key="financialSummary.quickAddShippingShipperHint"
      success-message-key="financialSummary.shippingCreatedFromProformOk"
      :lists="listsForModals"
      :parentID="parentID"
      :parentModel="parentModel"
      :extraParams="{}"
      :prefill-source-item="itemData"
      :prefill-source-model="modelName"
      :invoice-line-containers-allocated-by-index="
        balanceData && Array.isArray(balanceData.invoiceLineContainersAllocatedByIndex)
          ? balanceData.invoiceLineContainersAllocatedByIndex
          : []
      "
      :shipping-rows-for-container-slots="
        balanceData && Array.isArray(balanceData.shippings) ? balanceData.shippings : []
      "
      :balance-proform-snapshot="balanceData && balanceData.proform ? balanceData.proform : null"
      @close="closeQuickShippingShipperModal"
      @saved="handleQuickShippingShipperSaved"
    />

    <!-- Collection Details Modal -->
    <div 
      v-if="showCollectionDetailsModal" 
      class="modal fade show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="closeCollectionDetailsModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-list me-2"></i>
              {{ $t("financialSummary.collectionDetails") }}
            </h5>
            <button type="button" class="btn-close" @click="closeCollectionDetailsModal"></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="loadingCollectionDetails" variant="table" :rows="4" :cols="4" padded />
            <div v-else-if="collectionDetails">
              <!-- Summary -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.expectedAmount") }}</small>
                      <h5 class="mb-0 text-primary">{{ moneyPrefix }}{{ formatNumber(collectionDetails.expectedAmount) }}</h5>
                      <div
                        v-if="
                          collectionDetails.targetItem &&
                            Math.abs(Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) > 0.01
                        "
                        class="mt-2 small rounded border overflow-hidden"
                        role="note"
                      >
                        <div
                          class="px-2 py-1 text-uppercase fw-semibold border-bottom bg-light text-secondary"
                          style="font-size: 0.58rem; letter-spacing: 0.04em"
                        >
                          <i class="fas fa-file-invoice me-1" aria-hidden="true"></i>
                          {{ $t("financialSummary.stages.notaImpactBoxCaption") }}
                        </div>
                        <div
                          class="px-2 py-2 fw-semibold tabular-nums"
                          :class="
                            (Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) >= 0
                              ? 'text-success border-start border-success border-3'
                              : 'text-danger border-start border-danger border-3'
                          "
                        >
                          {{
                            $t("financialSummary.stages.notaImpactExpectedNet", {
                              amount:
                                (Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) >= 0
                                  ? "-" + moneyPrefix + formatNumber(collectionDetails.targetItem.point1CreditNotesGiven)
                                  : "+" + moneyPrefix + formatNumber(
                                      Math.abs(Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0)
                                    ),
                            })
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.collectedAmount") }}</small>
                      <h5 class="mb-0 text-success">
                        {{ moneyPrefix }}{{ formatNumber(collectionDetails.collectedTotal !== undefined ? collectionDetails.collectedTotal : collectionDetails.collectedAmount) }}
                      </h5>
                      <small
                        v-if="collectionDetails.targetItem && collectionDetails.targetItem.type === 'shipping'"
                        class="text-muted d-block"
                      >
                        {{ $t("financialSummary.shippingCollectionsApplied") }}: {{ moneyPrefix }}{{ formatNumber(collectionDetails.collectedAmount || 0) }}
                        <span v-if="(collectionDetails.targetItem.proformAdvanceDeducted || 0) > 0">
                          | {{ $t("financialSummary.advanceDeducted") }}: {{ moneyPrefix }}{{ formatNumber(collectionDetails.targetItem.proformAdvanceDeducted || 0) }}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Collections Table -->
              <div v-if="collectionDetails.collections && collectionDetails.collections.length > 0">
                <h6 class="mb-2">
                  <i class="fas fa-receipt me-2"></i>
                  {{ $t("financialSummary.collections") }} ({{ collectionDetails.collections.length }})
                </h6>
                <div class="table-responsive">
                  <table class="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>{{ $t("financialSummary.name") }}</th>
                        <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                        <th>{{ $t("financialSummary.status") }}</th>
                        <th>{{ $t("financialSummary.date") }}</th>
                        <th>{{ $t("financialSummary.actionsColumn") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="collection in collectionDetails.collections" :key="collection._id">
                        <td>{{ collection.name || collection.idModel }}</td>
                        <td class="text-end fw-bold text-success">
                          {{ moneyPrefix }}{{ formatNumber(collection.appliedAmount !== undefined ? collection.appliedAmount : collection.amount) }}
                        </td>
                        <td>
                          <span 
                            class="badge" 
                            :class="collection.received ? 'bg-success' : 'bg-warning'"
                          >
                            {{ collection.received ? $t("financialSummary.received") : $t("financialSummary.pending") }}
                          </span>
                        </td>
                        <td>{{ formatDate(collection.collection_date || collection.createdAt) }}</td>
                        <td>
                          <div class="btn-group btn-group-sm">
                            <button 
                              class="btn btn-outline-success" 
                              @click="openReceiptModal(collection._id)"
                              :title="$t('financialSummary.viewReceipt')"
                            >
                              <i class="fas fa-receipt"></i>
                            </button>
                            <button 
                              class="btn btn-outline-primary" 
                              @click="goToCollect(collection._id)"
                              :title="$t('financialSummary.viewDetails')"
                            >
                              <i class="fas fa-external-link-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-3">
                <i class="fas fa-inbox fa-2x text-muted mb-2"></i>
                <p class="text-muted mb-0">{{ $t("financialSummary.noCollections") }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCollectionDetailsModal">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

        <ReferenceModal
      v-if="showAddShippingFromProformModal"
      :is-visible="showAddShippingFromProformModal"
      model-name="shipping"
      :model-label="$t('Shipping')"
      :lists="listsForModals"
      :models="[]"
      :visual-groups="[]"
      :parentID="itemData && itemData._id ? String(itemData._id) : ''"
      :selectedModel="modelName"
      :editing-item="addShippingFromProformEditingItem"
      @close="closeAddShippingFromProformModal"
      @saved="handleAddShippingFromProformSaved"
    />

    <QuickShipperUnitPricesModal
      :is-visible="showShipperPricesModal"
      :shipping-id="shipperPricesModalShippingId"
      :shipping-name="shipperPricesModalShippingName"
      @close="closeShipperUnitPricesModal"
      @saved="onShipperUnitPricesSaved"
    />

    <QuickShipperInvoiceUnitQtyModal
      :is-visible="showShipperInvoiceQtyModal"
      :shipping-id="shipperInvoiceQtyModalShippingId"
      :shipping-name="shipperInvoiceQtyModalShippingName"
      @close="closeShipperInvoiceUnitQtyModal"
      @saved="onShipperInvoiceUnitQtySaved"
    />

    <QuickInvoiceUnitQtyModal
      :is-visible="showInvoiceUnitQtyModal"
      :shipping-id="invoiceUnitQtyModalShippingId"
      :shipping-name="invoiceUnitQtyModalShippingName"
      @close="closeInvoiceUnitQtyModal"
      @saved="onInvoiceUnitQtySaved"
    />

    <QuickAutoloadExpensesModal
      :is-visible="showAutoloadExpensesModal"
      :shipping-id="autoloadExpensesModalShippingId"
      :shipping-name="autoloadExpensesModalShippingName"
      :lists="listsForModals"
      @close="closeAutoloadExpensesModal"
      @saved="onAutoloadExpensesSaved"
    />

    <QuickAutoloadPackingListModal
      :is-visible="showAutoloadPackingListModal"
      :shipping-id="autoloadPackingListModalShippingId"
      :shipping-name="autoloadPackingListModalShippingName"
      :silent-autoload-invoice-supplier-after-packinglist="
        silentAutoloadInvoiceSupplierAfterPackinglist
      "
      @close="closeAutoloadPackingListModal"
      @saved="onAutoloadPackingListSaved"
    />

    <QuickEditCollectModal
      ref="quickEditCollectModal"
      :lists="listsForModals"
      @saved="$emit('refresh')"
    />

    <NotaComercialNoteModal
      ref="notaComercialNoteModal"
      :lists="listsForModals"
      @saved="$emit('refresh')"
    />

    <div
      v-if="showFobNcEditModal && fobNotaComercialShortcuts"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
      @click.self="closeFobNcEditModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-pencil-alt me-2" aria-hidden="true"></i>
              {{ $t("financialSummary.fobNotaComercial.editModalTitle") }}
            </h5>
            <button type="button" class="btn-close" :aria-label="$t('close')" @click="closeFobNcEditModal"></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="fobNcEditFetching" variant="form" :rows="4" />
            <template v-else>
              <div class="small text-muted mb-3">
                {{ fobNcEditAppliedHintText }}
              </div>
              <div v-if="fobNcEditDoc" class="mb-2 small text-body text-break">
                <span class="text-muted">{{ $t("financialSummary.name") }}:</span>
                {{ fobNcEditDoc.name || "—" }}
              </div>
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label small">{{ $t("financialSummary.fobNotaComercial.amountLabel") }}</label>
                  <input
                    v-model="fobNcEditAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control form-control-sm"
                    :disabled="fobNcEditSaving || fobNcEditDeleting"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small">{{ $t("financialSummary.fobNotaComercial.dateLabel") }}</label>
                  <input
                    v-model="fobNcEditDate"
                    type="date"
                    class="form-control form-control-sm"
                    :disabled="fobNcEditSaving || fobNcEditDeleting"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small">{{ $t("financialSummary.fobNotaComercial.currencyLabel") }}</label>
                  <select
                    v-model="fobNcEditCurrencyId"
                    class="form-select form-select-sm"
                    :disabled="fobNcEditSaving || fobNcEditDeleting"
                  >
                    <option v-for="c in fobNcCurrencyRows()" :key="c._id" :value="String(c._id)">
                      {{ c.label || c._id }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label small">{{ $t("Comment") }}</label>
                  <textarea
                    v-model="fobNcEditComment"
                    class="form-control form-control-sm"
                    rows="2"
                    :disabled="fobNcEditSaving || fobNcEditDeleting"
                  ></textarea>
                </div>
              </div>
            </template>
          </div>
          <div class="modal-footer d-flex flex-wrap gap-2 justify-content-between">
            <button
              type="button"
              class="btn btn-outline-danger"
              :disabled="fobNcEditSaving || fobNcEditDeleting || fobNcEditFetching"
              @click="deleteFobNcEditNote"
            >
              <span
                v-if="fobNcEditDeleting"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("financialSummary.fobNotaComercial.deleteButton") }}
            </button>
            <div class="d-flex gap-2 ms-auto">
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="fobNcEditSaving || fobNcEditDeleting || fobNcEditFetching"
                @click="closeFobNcEditModal"
              >
                {{ $t("financialSummary.fobNotaComercial.cancel") }}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="fobNcEditSaving || fobNcEditDeleting || fobNcEditFetching"
                @click="submitFobNcEdit"
              >
                <span
                  v-if="fobNcEditSaving"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ fobNcEditSaving ? $t("financialSummary.fobNotaComercial.saving") : $t("financialSummary.fobNotaComercial.save") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import financialCollectionStagesMixin from "./mixins/financialCollectionStagesMixin.js";
import fobNotaComercialQuickMixin from "./mixins/fobNotaComercialQuickMixin.js";
import operationCurrencyMixin from "./mixins/operationCurrencyMixin.js";
import NotaComercialNoteModal from "@/modules/nota_comercial_module/components/NotaComercialNoteModal.vue";
import NotaComercialPendingBadge from "@/modules/nota_comercial_module/components/NotaComercialPendingBadge.vue";
import NotaComercialAssignButton from "@/modules/nota_comercial_module/components/NotaComercialAssignButton.vue";
import QuickAddCollectModal from "./common/QuickAddCollectModal.vue";
import QuickAutoloadExpensesModal from "./common/QuickAutoloadExpensesModal.vue";
import QuickAutoloadPackingListModal from "./common/QuickAutoloadPackingListModal.vue";
import QuickShipperUnitPricesModal from "./common/QuickShipperUnitPricesModal.vue";
import QuickShipperInvoiceUnitQtyModal from "./common/QuickShipperInvoiceUnitQtyModal.vue";
import QuickInvoiceUnitQtyModal from "./common/QuickInvoiceUnitQtyModal.vue";
import QuickEditCollectModal from "./common/QuickEditCollectModal.vue";
import ReferenceModal from "./common/ReferenceModal.vue";
import ProformAdvanceModal from "./common/ProformAdvanceModal.vue";
import OperationFlagsCard from "./common/OperationFlagsCard.vue";
import {
  normalizeFinancialCategories,
  openFlagColorPopup,
  openNewFlagPopup,
  resolveFinancialCategoryColor,
} from "@/utils/financialMovementCategory";
import { wencesApi } from "@/api/wencesApi";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";
import { expensesArePaidWithoutRequest } from "@/utils/expensePayMode";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "CollectionSummary",
  mixins: [
    financialCollectionStagesMixin,
    fobNotaComercialQuickMixin,
    operationCurrencyMixin,
  ],
  components: {
    ContentSkeleton,
    NotaComercialPendingBadge,
    NotaComercialAssignButton,
    ProformAdvanceModal,
    QuickAddCollectModal,
    QuickAutoloadExpensesModal,
    QuickAutoloadPackingListModal,
    QuickShipperUnitPricesModal,
    QuickShipperInvoiceUnitQtyModal,
    QuickInvoiceUnitQtyModal,
    QuickEditCollectModal,
    ReferenceModal,
    NotaComercialNoteModal,
    OperationFlagsCard,
  },
  props: {
    modelName: { type: String, required: true },
    itemData: { type: Object, required: true },
    balanceData: { type: Object, default: null },
    financialData: { type: Object, required: true },
    canCollect: { type: Boolean, default: false },
    /** Permite abrir el popup de precios unitarios shipper (productandpriceshipper) cuando la factura proveedor va en $0. */
    canEditShipperPrices: { type: Boolean, default: false },
    /** Cantidades factura cliente (productandprice…unit_qty) cuando Total invoice del embarque va en $0. */
    canEditInvoiceUnitQty: { type: Boolean, default: false },
    /** Popup «Autoload expenses» en pagos (misma pantalla que etapa 2). */
    canAutoloadExpenses: { type: Boolean, default: false },
    /**
     * FOB Trading: botón para cargar los costos de la negociación que la operación todavía
     * no tiene como gasto (emite load-negotiation-expenses; si no hay nada, avisa el padre).
     */
    canLoadNegotiationExpenses: { type: Boolean, default: false },
    loadingNegotiationExpenses: { type: Boolean, default: false },
    /** Botón «Pedir pago» por gasto (ReferenceModal payment sobre shipping_expenses). */
    canMakePayments: { type: Boolean, default: false },
    /** Botón «Pay» / marcar pago como pagado (`paid`): solo `finance.manage` (mismo criterio que administrar finanzas). */
    canFinanceManagePayments: { type: Boolean, default: false },
    /** Borrar pagos (`payment.manage`): mismo permiso que pide el endpoint de borrado. */
    canDeletePayments: { type: Boolean, default: false },
    lists: { type: Array, default: () => [] },
    /** Model name passed as parent when creating a collect on a shipping (stage 2); must match hooks/lists. */
    shippingCollectParentModel: { type: String, default: "shipping" },
    /**
     * FOB Trading: tras autoload packing list guardado, aplicar invoice supplier en segundo plano (sin avisos).
     */
    silentAutoloadInvoiceSupplierAfterPackinglist: { type: Boolean, default: false },
    /** FOB Trading: atajos de alta de nota comercial junto a cliente (etapa 1) y proveedor (etapa 2). */
    fobNotaComercialShortcuts: { type: Boolean, default: false },
    /** Módulo nota_comercial_module activo en el tenant. */
    notaComercialModuleActive: { type: Boolean, default: false },
  },
  data() {
    return {
      /** Catálogo de flags del modelo de la operación (mismo que el listado). */
      operationCategoryCatalog: [],
      /** Id de la lista de banderas del tenant, para linkear a su edición. */
      operationFlagListId: "",
      /**
       * Campo del documento donde vive el flag. Es `category` salvo que el modelo ya
       * lo use como campo propio (melinka: Category de producto); ahí va en
       * `flag_category` para no pisar ese dato. Lo decide el backend.
       */
      operationFlagField: "flag_category",
      /** Campo del que se leen los flags previos a `flag_category` (o null). */
      operationLegacyFlagField: null,
      /** Flag elegido en esta pantalla, hasta que el padre recargue itemData. */
      operationCategoryOverride: null,
      operationFlagSaving: false,
      /** Banderas elegidas por embarque (id → nombres), hasta que recargue la ficha. */
      shippingFlagsOverride: {},
      /** Embarques con un guardado de banderas en curso (id → true). */
      shippingFlagSavingMap: {},
      /** Observaciones de la operación: borrador local + estado del autoguardado. */
      observationsDraft: "",
      observationsStatus: "",
      observationsTimer: null,
      observationsSavedValue: "",
    };
  },
  mounted() {
    this.loadOperationFlagCatalog();
    this.resetObservationsDraft();
  },
  beforeDestroy() {
    if (this.observationsTimer) clearTimeout(this.observationsTimer);
  },
  watch: {
    "itemData._id": function () {
      this.operationCategoryOverride = null;
      this.shippingFlagsOverride = {};
      this.shippingFlagSavingMap = {};
      this.loadOperationFlagCatalog();
      this.resetObservationsDraft();
    },
  },
  computed: {
    /** Proforma en pantalla: es el destino de las notas del cliente en la etapa 1. */
    fobNcProformId() {
      return this.itemData && this.itemData._id ? String(this.itemData._id) : "";
    },
    /** Nibiru paga de una sola vez: el botón dice «Pay» y no «Request payment». */
    expensesPaidWithoutRequest() {
      return expensesArePaidWithoutRequest();
    },
    isDuntowerTenant() {
      return isDuntowerTenant();
    },
    /**
     * El nombre del embarque lleva a la operación FOB en la misma pestaña. Duntower
     * prefiere la vista del shipping en otra pestaña, y dentro de la propia operación
     * no hay a dónde ir.
     */
    stage2ShippingNameOpensOperacion() {
      return (
        this.fobNotaComercialShortcuts &&
        !this.isDuntowerTenant &&
        !!this.fobNcProformId &&
        !(this.$route && this.$route.name === "FobtradingOperacion")
      );
    },
    /**
     * Las banderas se etiquetan por embarque (etapa 2), no en la proforma: el catálogo
     * se carga con el modelo de la operación pero la lista de flags es una sola.
     */
    operationFlagsEnabled() {
      return !!(this.modelName && this.itemData && this.itemData._id);
    },
    /** Modelo al que se le asignan las banderas de la etapa 2: el embarque. */
    shippingFlagModelName() {
      return "shipping";
    },
    notaComercialQuickAllowed() {
      if (!this.notaComercialModuleActive) return false;
      return !!(this.canCollect || this.canMakePayments || this.canFinanceManagePayments);
    },
  },
  methods: {
    openStage2ShippingName(ship) {
      if (!this.stage2ShippingNameOpensOperacion) {
        this.goToShipping(ship && ship.shippingId);
        return;
      }
      this.$router.push({
        name: "FobtradingOperacion",
        params: { proformId: this.fobNcProformId },
      });
    },
    /** Al aplicar una nota que ya existía, los totales de la pantalla quedaron viejos. */
    onNotaComercialAssigned() {
      this.$emit("refresh");
    },
    resetObservationsDraft() {
      const value = (this.itemData && this.itemData.observations) || "";
      this.observationsDraft = String(value);
      this.observationsSavedValue = this.observationsDraft;
      this.observationsStatus = "";
    },
    /** Autoguardado: se dispara al dejar de escribir (y al salir del campo). */
    onObservationsInput() {
      if (this.observationsTimer) clearTimeout(this.observationsTimer);
      this.observationsTimer = setTimeout(() => {
        this.observationsTimer = null;
        this.saveObservations();
      }, 900);
    },
    async saveObservations() {
      if (this.observationsTimer) {
        clearTimeout(this.observationsTimer);
        this.observationsTimer = null;
      }
      if (!this.operationFlagsEnabled) return;
      const value = String(this.observationsDraft || "");
      if (value === this.observationsSavedValue) return;
      this.observationsStatus = "saving";
      try {
        // Endpoint de campo puntual: no dispara las validaciones del formulario completo.
        await wencesApi.put(
          `/api/gotrading/dynamic/field/${encodeURIComponent(
            this.modelName
          )}/${encodeURIComponent(this.itemData._id)}`,
          { observations: value, partialUpdate: true }
        );
        this.observationsSavedValue = value;
        this.observationsStatus = "saved";
        if (this.itemData) this.$set(this.itemData, "observations", value);
        setTimeout(() => {
          if (this.observationsStatus === "saved") this.observationsStatus = "";
        }, 2500);
      } catch (e) {
        this.observationsStatus = "error";
      }
    },
    /**
     * Catálogo de banderas de los embarques: va con el modelo del embarque, no con el de
     * la pantalla. Cada modelo tiene su propia lista de banderas, así que pedirlo con el
     * modelo de la proforma mostraba las banderas de otra lista que el guardado rechaza.
     */
    async loadOperationFlagCatalog() {
      if (!this.operationFlagsEnabled) return;
      try {
        const { data } = await wencesApi.get("/api/financial/categories", {
          params: { modelName: this.shippingFlagModelName },
        });
        this.operationCategoryCatalog = Array.isArray(data?.data) ? data.data : [];
        if (data?.flagField) this.operationFlagField = data.flagField;
        this.operationFlagListId = data?.flagListId ? String(data.flagListId) : "";
        this.operationLegacyFlagField = data?.legacyCategoryField || null;
      } catch (e) {
        this.operationCategoryCatalog = [];
      }
    },
    /** Guarda las banderas elegidas en el select de la tarjeta. */
    /** Clave del mapa de banderas por embarque. */
    shippingFlagKey(ship) {
      return String((ship && ship.shippingId) || "");
    },
    /** Banderas del embarque: lo elegido acá, o lo que trae el documento. */
    shippingFlagCategories(ship) {
      const key = this.shippingFlagKey(ship);
      const override = key ? this.shippingFlagsOverride[key] : null;
      if (Array.isArray(override)) return override;
      // `flag_category` es un campo de tipo list: llega como ids, que se resuelven
      // contra el catálogo (los valores de la lista única de banderas).
      return normalizeFinancialCategories(
        ship && ship.flag_category,
        this.operationCategoryCatalog
      );
    },
    isShippingFlagSaving(ship) {
      const key = this.shippingFlagKey(ship);
      return !!(key && this.shippingFlagSavingMap[key]);
    },
    /** Guarda las banderas elegidas en la tarjeta de un embarque. */
    async saveShippingFlags(ship, nextCategories) {
      const key = this.shippingFlagKey(ship);
      if (!key) return;
      const previous = this.shippingFlagCategories(ship);
      // Optimista: la tarjeta ya muestra lo elegido mientras se guarda.
      this.$set(this.shippingFlagsOverride, key, nextCategories);
      this.$set(this.shippingFlagSavingMap, key, true);
      try {
        const saved = await this.persistShippingFlags(key, nextCategories);
        this.$set(this.shippingFlagsOverride, key, saved);
        await this.loadOperationFlagCatalog();
      } catch (e) {
        this.$set(this.shippingFlagsOverride, key, previous);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: e.response?.data?.error || e.message,
        });
      } finally {
        this.$set(this.shippingFlagSavingMap, key, false);
      }
    },
    /**
     * Deja el embarque con exactamente estas banderas. Las que no estén en el catálogo
     * se crean; el color de cada bandera sale del popup del badge o de /lists.
     */
    async persistShippingFlags(shippingId, nextCategories) {
      const list = (Array.isArray(nextCategories) ? nextCategories : [])
        .map((n) => String(n || "").trim())
        .filter(Boolean);
      // El select solo ofrece banderas del catálogo: acá se asigna, nunca se da de alta
      // una nueva (se administran en /lists, ticket #383).
      const body = {
        categories: list,
        category: list[0] || "",
        modelName: this.shippingFlagModelName,
        // El flag vive en `flag_category`: nunca pisa el `category` del modelo.
        allowFlagFallback: true,
      };
      const { data } = await wencesApi.put(
        `/api/financial/categories/assign/${encodeURIComponent(
          this.shippingFlagModelName
        )}/${encodeURIComponent(shippingId)}`,
        body
      );
      if (!data?.success) {
        throw new Error(data?.error || "Failed to save category");
      }
      const saved = data.data?.assignedCategories;
      return Array.isArray(saved) ? saved : list;
    },
    /**
     * "Agregar bandera nueva" del select: popup de nombre y color, se crea en el
     * catálogo (compartido con el listado) y queda asignada al embarque.
     */
    async createShippingFlag(ship) {
      const picked = await openNewFlagPopup({
        t: (key) => this.$t(key),
        catalog: this.operationCategoryCatalog,
      });
      if (!picked || !picked.name) return;
      try {
        await wencesApi.post("/api/financial/categories", {
          name: picked.name,
          modelName: this.shippingFlagModelName,
          ...(picked.color ? { color: picked.color } : {}),
        });
        await this.loadOperationFlagCatalog();
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: e.response?.data?.error || e.message,
        });
        return;
      }
      const current = this.shippingFlagCategories(ship);
      if (current.some((n) => String(n).toLowerCase() === picked.name.toLowerCase())) return;
      await this.saveShippingFlags(ship, [...current, picked.name]);
    },
    /**
     * Clic en una bandera: popup del color. El color es de la bandera (la lista es una
     * sola), así que al guardarlo cambia en todas las pantallas que la muestran.
     */
    async openFlagColorPicker(name) {
      const flag = String(name || "").trim();
      if (!flag) return;
      const color = await openFlagColorPopup({
        t: (key) => this.$t(key),
        name: flag,
        currentColor: resolveFinancialCategoryColor(flag, this.operationCategoryCatalog),
        catalog: this.operationCategoryCatalog,
      });
      if (!color) return;
      try {
        await wencesApi.post("/api/financial/categories", {
          name: flag,
          modelName: this.shippingFlagModelName,
          color,
        });
        await this.loadOperationFlagCatalog();
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: e.response?.data?.error || e.message,
        });
      }
    },
    async openNotaComercialNoteModal() {
      // Abrir siempre: crédito en shipping cobrado se bloquea al guardar; débito sí se permite.
      const modal = this.$refs.notaComercialNoteModal;
      if (!modal || typeof modal.open !== "function") return;
      modal.open({
        sourceModel: this.modelName,
        modelName: this.modelName,
        itemData: this.itemData,
        clientId: this.collectionStagesResolvedClientId,
        lockContact: !!this.collectionStagesResolvedClientId,
        namePrefix: "NC-FIN",
      });
    },
    resolveNotaComercialCollectiblePending(ship = null) {
      if (ship) {
        const p = Number(ship.pendingToCollect);
        return Number.isFinite(p) ? Math.max(0, p) : 0;
      }
      const stages = this.collectionStagesVisual;
      if (stages && stages.stage1) {
        const s1 = Number(stages.stage1.remainingAmount) || 0;
        const agg =
          Number(this.financialData && this.financialData.aggregateShippingInvoicePending) || 0;
        return Math.max(0, s1, agg);
      }
      const fd = this.financialData;
      if (!fd) return 0;
      const pending = Number(fd.totalPending);
      if (Number.isFinite(pending)) return Math.max(0, pending);
      const total = Number(fd.totalProform) || 0;
      const collected = Number(fd.totalCollected) || 0;
      return Math.max(0, total - collected);
    },
    async ensureNotaComercialPendingAllowsCreate(ship = null) {
      const pending = this.resolveNotaComercialCollectiblePending(ship);
      if (pending > 0.01) return true;
      await Swal.fire({
        icon: "info",
        title: this.$tn("financialSummary.actions.notaComercialNoPendingTitle"),
        text: this.$tn("financialSummary.actions.notaComercialNoPendingText"),
      });
      return false;
    },
  },
};
</script>

<style scoped>
/* Contenedores de la operación: cantidad + número(s) de la lista de empaque.
   El cliente pidió la letra un poco más grande que el resto de la línea. */
.financial-summary-stages__containers {
  font-size: 0.95rem;
  line-height: 1.35;
}

.financial-summary-stages__containers-label {
  font-size: 0.78rem;
}

/* Los números de contenedor: monoespaciados para que se lean de un golpe. */
.financial-summary-stages__container-numbers {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92rem;
  letter-spacing: 0.01em;
  word-break: break-word;
}

.financial-summary-stages {
  background: linear-gradient(145deg, rgba(13, 110, 253, 0.04) 0%, #fff 35%, #f8f9fa 100%);
  border-color: rgba(13, 110, 253, 0.18) !important;
}

.financial-summary-stages__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.35rem;
  border-radius: 0.35rem;
  font-weight: 800;
  font-size: 0.85rem;
  background: #0d6efd;
  color: #fff;
}

/* % de adelanto proforma (cliente) vs proveedor: resalta frente al resto del texto */
.financial-summary-stages__advance-pct-emphasis {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  display: inline-block;
  padding: 0.04em 0.32em;
  border-radius: 0.28rem;
  line-height: 1.25;
}

.financial-summary-stages__advance-pct-emphasis--proform {
  color: #0a58ca;
  background: rgba(13, 110, 253, 0.14);
}

.financial-summary-stages__advance-pct-emphasis--supplier {
  color: #b54700;
  background: rgba(253, 126, 20, 0.2);
}

/* Lápiz para editar el % de adelanto: discreto al lado de la insignia, se afirma al pasar por encima. */
.financial-summary-stages__advance-pct-edit {
  color: #0a58ca;
  opacity: 0.55;
  font-size: 0.75rem;
  text-decoration: none;
  vertical-align: baseline;
}

.financial-summary-stages__advance-pct-edit:hover,
.financial-summary-stages__advance-pct-edit:focus-visible {
  opacity: 1;
  color: #0a58ca;
}

.financial-summary-stages__advance-pct-edit:disabled {
  opacity: 0.35;
}

/* Botón de cargar el % dentro del cartel naranja: blanco sobre el fondo del aviso. */
.financial-summary-stages__advance-pct-set {
  --bs-btn-color: #8a4b00;
  --bs-btn-hover-color: #6d3b00;
  --bs-btn-bg: #fff;
  --bs-btn-hover-bg: #fff;
  --bs-btn-border-color: rgba(138, 75, 0, 0.25);
  --bs-btn-hover-border-color: rgba(138, 75, 0, 0.55);
  white-space: nowrap;
}

.financial-summary-stages__badge--secondary {
  background: #6c757d;
}

.financial-summary-stages__badge--done {
  background: #198754 !important;
  color: #fff !important;
}

.financial-summary-stages__badge--secondary.financial-summary-stages__badge--done {
  background: #198754 !important;
}

.financial-summary-stages__client-under-step1:hover {
  text-decoration: underline !important;
}

.financial-summary-stages__supplier-contact-link:hover {
  text-decoration: underline !important;
}

.financial-summary-stages__col--done {
  background: rgba(25, 135, 84, 0.07);
}

.financial-summary-stages__stage-head--done {
  padding-bottom: 0.15rem;
}

.financial-summary-stages__col1 {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

@media (min-width: 992px) {
  .financial-summary-stages__col1 {
    border-bottom: none;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
}

.financial-summary-stages__ship {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 0.08rem 0.35rem rgba(0, 0, 0, 0.04);
}

/* Embarque: cobro completo (adelanto + factura) — mismo criterio que etapa 1 “done” */
.financial-summary-stages__ship--complete {
  background: rgba(25, 135, 84, 0.08);
  border-color: rgba(25, 135, 84, 0.38) !important;
  box-shadow: 0 0.12rem 0.45rem rgba(25, 135, 84, 0.14);
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-section--invoice {
  background: rgba(25, 135, 84, 0.1);
  border-left-color: #198754;
}

.financial-summary-stages__ship--complete
  .financial-summary-stages__ship-section--invoice
  .financial-summary-stages__ship-section-title {
  color: #198754 !important;
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-name-link {
  color: #146c43 !important;
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-name-link:hover {
  color: #0f5132 !important;
}

.financial-summary-stages__ship--placeholder {
  border: 2px dashed rgba(108, 117, 125, 0.65);
  background: rgba(248, 249, 250, 0.85);
  box-shadow: none;
  min-height: 6.5rem;
}

.financial-summary-stages__missing-shipping-body {
  white-space: pre-line;
}

.financial-summary-stages__ship-name-link {
  text-decoration: none;
  cursor: pointer;
}

.financial-summary-stages__ship-name-link:hover {
  color: var(--bs-primary) !important;
  text-decoration: underline;
}

.financial-summary-stages__ship-timeline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.45rem;
  border: 1px dashed rgba(13, 110, 253, 0.3);
  border-radius: 0.45rem;
  background: rgba(13, 110, 253, 0.03);
}

.financial-summary-stages__ship-timeline-step {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(108, 117, 125, 0.35);
  border-radius: 999px;
  padding: 0.16rem 0.55rem;
  font-size: 0.72rem;
  line-height: 1.2;
  color: #495057;
  background: #fff;
}

button.financial-summary-stages__ship-timeline-step {
  cursor: default;
}

button.financial-summary-stages__ship-timeline-step.financial-summary-stages__ship-timeline-step--clickable {
  cursor: pointer;
}

button.financial-summary-stages__ship-timeline-step.financial-summary-stages__ship-timeline-step--clickable:hover {
  border-color: rgba(13, 110, 253, 0.55);
  color: #0d6efd;
}

.financial-summary-stages__ship-timeline-step--done {
  border-color: rgba(25, 135, 84, 0.5);
  color: #146c43;
  background: rgba(25, 135, 84, 0.08);
}

.financial-summary-stages__ship-timeline-sep {
  color: #adb5bd;
  font-size: 0.72rem;
  display: inline-flex;
  align-items: center;
}

.financial-summary-stages__ship-timeline-step-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  line-height: 1.2;
  color: #6c757d;
  padding: 0 0.2rem;
}

.financial-summary-stages__ship-timeline-step-label--done {
  color: #146c43;
  font-weight: 600;
}

/* Etapa 2: un embarque por fila (100%) — dentro, cobros (izq., drag) | pagos (der.). */
.financial-summary-stages__ship-stack {
  align-items: stretch;
}

.financial-summary-stages__ship-stack > .financial-summary-stages__ship--full {
  flex: 1 1 100%;
  max-width: 100%;
  min-width: 0;
}

.financial-summary-stages__ship-split {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 0.5rem;
  margin-top: 0.25rem;
}

@media (min-width: 768px) {
  .financial-summary-stages__ship-collections {
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
}

@media (max-width: 767.98px) {
  .financial-summary-stages__ship-payments {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 0.5rem;
    margin-top: 0.25rem;
  }
}

.financial-summary-stages__ship-section--payments {
  background: rgba(220, 53, 69, 0.06);
  border-left: 3px solid rgba(220, 53, 69, 0.45);
  border-radius: 0.35rem;
  padding: 0.5rem 0.55rem;
}

.financial-summary-stages__ship-section--payments.financial-summary-stages__ship-section--payments-complete {
  background: rgba(25, 135, 84, 0.12);
  border-left-color: rgba(25, 135, 84, 0.75);
}

.financial-summary-stages__ship--compact-stage2 {
  background: rgba(25, 135, 84, 0.11);
  border-color: rgba(25, 135, 84, 0.42) !important;
  box-shadow: 0 0.1rem 0.4rem rgba(25, 135, 84, 0.12);
}

.financial-summary-stages__ship-compact-summary {
  background: rgba(25, 135, 84, 0.16);
  border: 1px solid rgba(25, 135, 84, 0.38);
  border-radius: 0.4rem;
  padding: 0.5rem 0.65rem;
  cursor: pointer;
  user-select: none;
}

.financial-summary-stages__ship-compact-summary:hover {
  background: rgba(25, 135, 84, 0.22);
  border-color: rgba(25, 135, 84, 0.5);
}

.financial-summary-stages__ship-compact-summary:focus {
  outline: 2px solid rgba(13, 110, 253, 0.45);
  outline-offset: 2px;
}

.financial-summary-stages__ship-compact-summary__legend {
  letter-spacing: 0.02em;
}

.financial-summary-stages__progress {
  border-radius: 0.25rem;
  background-color: #e9ecef;
}

/* Barra de pagos etapa 2: dos tramos (info = pagado, warning = solicitado sin paid). */
.financial-summary-stages__progress--payments-split {
  overflow: hidden;
}
.financial-summary-stages__progress--payments-split .progress-bar {
  box-shadow: none;
}
.financial-summary-stages__progress--payments-split .progress-bar.bg-warning {
  color: inherit;
}

.financial-summary-stages__chip {
  cursor: grab;
}

.financial-summary-stages__chip:active {
  cursor: grabbing;
}

.financial-summary-stages__chip--stage1-collect[draggable="true"] {
  cursor: grab;
}
.financial-summary-stages__chip--stage1-collect[draggable="true"]:active {
  cursor: grabbing;
}
.financial-summary-stages__stage1-assign-ship-btn {
  font-size: 0.65rem;
  line-height: 1.25;
  max-width: 100%;
  min-width: 0;
}

.financial-summary-stages__chip-mini {
  cursor: grab;
  max-width: 7rem;
}

/* Cobros proforma (etapa 1): mismo acento verde que filas de cobro. */
.financial-summary-stages__chip--stage1-collect {
  border-left: 3px solid #198754 !important;
}

/* Cobros etapa 2: div/a (no <button>): <button draggable> no dispara drag en Chrome/Safari. */
.financial-summary-stages__chip--collect-row,
a.financial-summary-stages__chip--collect-row {
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.35rem;
  background: #fff;
  box-shadow: 0 0.06rem 0.2rem rgba(0, 0, 0, 0.04);
  text-align: left;
  line-height: 1.25;
  cursor: grab;
  color: inherit;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
/* Cobros: borde izquierdo verde (success). Notas comerciales: borde info. */
.financial-summary-stages__chip--collect-row:not(.financial-summary-stages__chip--collect-row--nota),
a.financial-summary-stages__chip--collect-row:not(.financial-summary-stages__chip--collect-row--nota) {
  border-left: 3px solid #198754;
}
.financial-summary-stages__chip--collect-row--nota,
a.financial-summary-stages__chip--collect-row--nota {
  border-left: 3px solid #0dcaf0;
}
.financial-summary-stages__chip--collect-row:hover,
a.financial-summary-stages__chip--collect-row:hover {
  box-shadow: 0 0.1rem 0.28rem rgba(0, 0, 0, 0.07);
  border-color: rgba(0, 0, 0, 0.16);
}
.financial-summary-stages__chip--collect-row:hover:not(.financial-summary-stages__chip--collect-row--nota),
a.financial-summary-stages__chip--collect-row:hover:not(.financial-summary-stages__chip--collect-row--nota) {
  border-left-color: #198754;
}
.financial-summary-stages__chip--collect-row--nota:hover,
a.financial-summary-stages__chip--collect-row--nota:hover {
  border-left-color: #0dcaf0;
}
.financial-summary-stages__chip--collect-row:focus-visible,
a.financial-summary-stages__chip--collect-row:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.2);
}
/* Productos de la factura del embarque, debajo de la cantidad de contenedores. */
.financial-summary-stages__ship-product {
  font-size: 0.7rem;
  line-height: 1.25;
  padding: 0.05rem 0.35rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.35rem;
  background: #f8f9fa;
  color: #495057;
  max-width: 100%;
}
.financial-summary-stages__ship-ports {
  font-size: 0.7rem;
  line-height: 1.25;
  padding: 0.05rem 0.35rem;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 0.35rem;
  color: #495057;
  max-width: 100%;
}
.financial-summary-stages__chip--collect-row.financial-summary-stages__chip--readonly {
  cursor: default;
}
.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only {
  cursor: default;
}
.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only:active {
  cursor: default;
}
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only {
  cursor: pointer;
}
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only:active {
  cursor: pointer;
}
/* NC: arrastre proforma ↔ embarque (cursor grab pese a chip-invoice-only). */
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only.financial-summary-stages__nota-proform-drag,
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only.financial-summary-stages__nota-shipping-drag {
  cursor: grab;
}
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only.financial-summary-stages__nota-proform-drag:active,
a.financial-summary-stages__chip--collect-row.financial-summary-stages__chip-invoice-only.financial-summary-stages__nota-shipping-drag:active {
  cursor: grabbing;
}
.financial-summary-stages__chip--collect-row:active:not(.financial-summary-stages__chip-invoice-only) {
  cursor: grabbing;
}
.financial-summary-stages__chip--collect-row .financial-summary-stages__chip-row-icon,
a.financial-summary-stages__chip--collect-row .financial-summary-stages__chip-row-icon {
  margin-top: 0.125rem;
}
.financial-summary-stages__chip--collect-row .financial-summary-stages__chip-row-amount,
a.financial-summary-stages__chip--collect-row .financial-summary-stages__chip-row-amount {
  padding-top: 0.125rem;
}

.financial-summary-stages__dropzone {
  background: transparent;
}

.financial-summary-stages__dropzone--stage1-full {
  width: 100%;
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
}

.financial-summary-stages__dropzone--stage1-full.financial-summary-stages__drop-target--active {
  min-height: 4rem;
}

/* DnD: contorno punteado rojo solo sobre la zona de soltar (no toda la columna etapa 1). */
.financial-summary-stages__ship-drop-advance,
.financial-summary-stages__ship-drop-invoice {
  border-radius: 0.45rem;
  transition: outline-color 0.18s ease, outline-width 0.18s ease;
}

.financial-summary-stages__ship-drop-advance.financial-summary-stages__drop-target--active,
.financial-summary-stages__ship-drop-invoice.financial-summary-stages__drop-target--active {
  outline: 3px dotted #dc3545;
  outline-offset: 2px;
}

.financial-summary-stages__dropzone.financial-summary-stages__drop-target--active {
  background: transparent !important;
  border-style: dotted !important;
  border-width: 3px !important;
  border-color: #dc3545 !important;
  color: #842029;
}

.financial-summary-stages__ship-section {
  border-radius: 0.35rem;
  padding: 0.5rem 0.55rem;
}

.financial-summary-stages__ship-section-title {
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.financial-summary-stages__ship-section--advance {
  background: rgba(13, 202, 240, 0.09);
  border-left: 3px solid #0dcaf0;
}

.financial-summary-stages__ship-section--advance.financial-summary-stages__ship-section--advance-done {
  background: rgba(25, 135, 84, 0.1);
  border-left-color: #198754;
}

.financial-summary-stages__ship-section--invoice {
  background: rgba(108, 117, 125, 0.08);
  border-left: 3px solid #868e96;
}

.financial-summary-stages__ship-section--invoice.financial-summary-stages__ship-section--invoice-balanced {
  background: rgba(25, 135, 84, 0.08);
  border-left-color: #198754;
}

.financial-summary-stages__ship-section-dashed-footer {
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
}

/* Cobro solo factura embarque: solo lectura (no es chip arrastrable como el adelanto) */
.financial-summary-stages__invoice-collected-static {
  cursor: default;
  user-select: text;
}

.financial-summary-stages__chip-invoice-only {
  cursor: default;
}

.financial-summary-stages__chip-invoice-only:active {
  cursor: default;
}

/* Un cuadro por shipping_expense (proveedor u otro costo). */
.financial-summary-stages__ship-expense-card {
  flex: 1 1 100%;
  min-width: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.4rem;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0.06rem 0.2rem rgba(0, 0, 0, 0.04);
}
/* Factura del proveedor y sus tramos de «Divide payment», a la par. */
.financial-summary-stages__ship-expense-card--paired {
  flex: 1 1 calc(50% - 0.25rem);
}
.financial-summary-stages__ship-expense-card--paired .financial-summary-stages__ship-expense-card__head .text-truncate {
  white-space: normal;
}
.financial-summary-stages__ship-expense-card__head {
  background: rgba(248, 249, 250, 0.85);
  border-radius: 0.35rem 0.35rem 0 0;
}

.financial-summary-stages__ship-expense-card__amount-labels {
  font-size: 0.62rem;
  letter-spacing: 0.02em;
  line-height: 1.15;
}

.financial-summary-stages__ship-expense-card__amount-labels span,
.financial-summary-stages__ship-expense-card__amount-values span {
  min-width: 0;
}
/* Gasto cubierto al 100% (misma línea visual que cobro / factura embarque equilibrada). */
.financial-summary-stages__ship-expense-card--fully-paid {
  border-color: rgba(25, 135, 84, 0.42) !important;
  background: rgba(25, 135, 84, 0.07);
  box-shadow: 0 0.08rem 0.35rem rgba(25, 135, 84, 0.12);
  border-left: 3px solid #198754;
}
.financial-summary-stages__ship-expense-card--fully-paid .financial-summary-stages__ship-expense-card__head {
  background: rgba(25, 135, 84, 0.1);
}

/* Factura proveedor solo en papelera (soft delete): aviso visible en bloque pagos. */
.financial-summary-stages__ship-expense-card--in-trash {
  border-color: rgba(255, 193, 7, 0.95) !important;
  border-width: 2px;
  background: rgba(255, 193, 7, 0.09);
  box-shadow: 0 0.08rem 0.35rem rgba(255, 193, 7, 0.22);
}
.financial-summary-stages__ship-expense-card--in-trash .financial-summary-stages__ship-expense-card__head {
  background: rgba(255, 193, 7, 0.14);
}
.financial-summary-stages__ship-expense-in-trash-badge__text {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* Pastillas de pagos (solicitados, aún no marcados paid): sin drag. */
.financial-summary-stages__chip--payment {
  cursor: default;
}
.financial-summary-stages__chip--payment:active {
  cursor: default;
}

/* Pastillas de pagos: borde izquierdo verde si pagado, rojo si pendiente. */
.financial-summary-stages__chip--payment:not(.financial-summary-stages__chip--payment-paid) {
  border-left: 3px solid #dc3545 !important;
}
.financial-summary-stages__chip--payment-paid {
  border-left: 3px solid #198754 !important;
  border-color: rgba(25, 135, 84, 0.45) !important;
}
</style>
